import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import clientCandidateDataService from '../services/clientCandidateDataService';
import clientService from '../services/clientService';
import pipelineService from '../services/pipelineService';
import CallWidget from './CallWidget';
import ContactPickerModal from './ContactPickerModal';
import CampaignCustomizerModal from './CampaignCustomizerModal';
import AddToCampaignSidebar from './AddToCampaignSidebar';
import SuggestionsPanel from './SuggestionsPanel';
import ConversationsTab from './ConversationsTab';
import StageTimeline from './StageTimeline';
import AssignPills from './AssignPills';
import AvailabilitySection from './AvailabilitySection';
import { InterviewsSection } from './AvailabilitySection';
import { hasPermission } from '../utils/permissions';
import useSSE from '../hooks/useSSE';
import ColumnBasedLayout from './ColumnBasedLayout';
import EnhancedCandidateHeader from './EnhancedCandidateHeader';
import NotesPanel from './NotesPanel';
import Tooltip from './Tooltip';
import CardChat from './CardChat';
import CardDocuments from './CardDocuments';
import CardActivity from './CardActivity';
import AssignUsersModal from './AssignUsersModal';
import ContactsLinksWidget from './widgets/client/ContactsLinksWidget';
import CandidateTrackingWidget from './widgets/client/CandidateTrackingWidget';
import CompensationWidget from './widgets/client/CompensationWidget';
import PreferencesWidget from './widgets/client/PreferencesWidget';
import CustomFieldsWidget from './widgets/client/CustomFieldsWidget';
import PositionsWidget from './widgets/client/PositionsWidget';
import EducationWidget from './widgets/client/EducationWidget';
import PipelinesWidget from './widgets/client/PipelinesWidget';
import ClientDocumentsWidget from './widgets/client/ClientDocumentsWidget';

function getPageType() {
  try {
    const path = window.location.pathname;
    if (path.startsWith('/search/')) return 'search';
    if (path.startsWith('/candidate/') || path.startsWith('/candidates')) return 'candidates';
    if (path.startsWith('/pipeline/') || path.startsWith('/pipelines')) return 'pipeline';
    if (path.startsWith('/archive')) return 'archive';
    if (path.startsWith('/client/')) return 'client';
    return 'default';
  } catch { return 'default'; }
}
function storageKey(base) { return `${base}_${getPageType()}`; }
const STORAGE_BASES = { mainWidth: 'banyak_preview_width', feedbackWidth: 'banyak_feedback_width', presentationWidth: 'banyak_presentation_width', notesWidth: 'banyak_notes_width' };
function getSaved(base, fb) { try { const v = localStorage.getItem(storageKey(base)); return v ? Math.max(200, parseInt(v, 10)) : fb; } catch { return fb; } }
function persist(base, val) { try { localStorage.setItem(storageKey(base), String(Math.round(val))); } catch {} }

// Fixed standardized width for all non-Notes sidebars (feedback, presentation,
// opportunities). Notes remains independently resizable since it's the special
// sidebar that can take over the candidate profile width.
const SIDEBAR_FIXED_WIDTH = 420;

// Mobile breakpoint — below this, the preview panel switches to a vertical
// stack with sidebars rendered as a bottom drawer.
const MOBILE_BREAKPOINT = 768;

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return isMobile;
};

const ResizeHandle = ({ onMouseDown, isActive }) => (
  <div className={`flex-shrink-0 cursor-col-resize relative group transition-all duration-150 ${isActive ? 'bg-emerald-500' : 'bg-gray-300 hover:bg-emerald-500'}`}
    onMouseDown={onMouseDown} style={{ width: '8px', minWidth: '8px' }}>
    <div className="absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2 h-12 flex flex-col items-center justify-center gap-1.5 opacity-50 group-hover:opacity-100 transition-opacity">
      <div className="w-1 h-1 rounded-full bg-white" />
      <div className="w-1 h-1 rounded-full bg-white" />
      <div className="w-1 h-1 rounded-full bg-white" />
      <div className="w-1 h-1 rounded-full bg-white" />
    </div>
  </div>
);

const CandidatePreviewPanel = ({ candidateId, onClose, isOpen, profileBasePath, cardId, pipelineId, pipelineName, autoOpenFeedback, autoOpenPresentation, onCardDelete, onPrevCandidate, onNextCandidate, hasPrev = false, hasNext = false }) => {
  const panelRef = useRef(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Real-time updates — re-fetch panel data when SSE events arrive
  useSSE(pipelineId, {
    onStatusChanged: () => setRefreshTrigger(p => p + 1),
    onRatingUpdated: () => setRefreshTrigger(p => p + 1),
    onFeedbackAdded: () => setRefreshTrigger(p => p + 1),
    onCardMoved: () => setRefreshTrigger(p => p + 1),
    onMeetingUpdated: () => setRefreshTrigger(p => p + 1),
    onCandidateReactivated: () => setRefreshTrigger(p => p + 1),
  });
  const [editModeState, setEditModeState] = useState({});
  const [width, setWidth] = useState(() => getSaved(STORAGE_BASES.mainWidth, 800));
  // Sidebars (feedback / presentation / opportunities) are FIXED width now.
  // We keep the constants/state names for compatibility but they are no longer
  // user-resizable. Notes remains resizable since it can grow into main.
  const feedbackWidth = SIDEBAR_FIXED_WIDTH;
  const presentationWidth = SIDEBAR_FIXED_WIDTH;
  const [notesWidth, setNotesWidth] = useState(() => getSaved(STORAGE_BASES.notesWidth, 500));
  const [resizingTarget, setResizingTarget] = useState(null);
  // Remembered main width before a sidebar opened, so closing the sidebar can
  // restore the candidate profile to its previous size. One slot — overwritten
  // each time a sidebar opens, cleared when all sidebars close.
  // Restore-on-close snapshots are declared further down (mainBeforeSidebarRef,
  // mainBeforeNotesRef) so we can track each panel independently.
  const [isNotesPanelOpen, setIsNotesPanelOpen] = useState(false);
  const [isFeedbackPanelOpen, setIsFeedbackPanelOpen] = useState(!!autoOpenFeedback);
  const [schedulePrefill, setSchedulePrefill] = useState(null); // bridges availability hover-Schedule → InterviewsSection
  const [feedbackExpanded, setFeedbackExpanded] = useState(false);
  const userType = (() => { try { return JSON.parse(localStorage.getItem('user') || '{}').user_type; } catch { return ''; } })();
  const [isPresentationOpen, setIsPresentationOpen] = useState(!!autoOpenPresentation && userType !== 'client');
  const [isOpportunitiesOpen, setIsOpportunitiesOpen] = useState(false);

  // LinkedIn sync state
  const [linkedinSyncing, setLinkedinSyncing] = useState(false);
  const [linkedinSyncStatus, setLinkedinSyncStatus] = useState('');
  
  const [showCallWidget, setShowCallWidget] = useState(false);
  const [callPhoneNumber, setCallPhoneNumber] = useState('');
  const [showPhonePicker, setShowPhonePicker] = useState(false);
  const [candidatePhones, setCandidatePhones] = useState([]);

  // Populate phones from the profile's contact data as soon as it's available,
  // so the SMS picker (and others) have them without needing to open Call.
  useEffect(() => {
    const p = profile?.client_data?.phones;
    if (Array.isArray(p)) {
      const filtered = p.filter(x => x && x.value);
      if (filtered.length) setCandidatePhones(filtered);
    }
  }, [profile]);

  // Expandable vertical nav column. Collapsed shows icon-only buttons
  // (~40px wide); expanded adds labels to the right of each icon so
  // new users can learn what each panel is. Preference persists to
  // localStorage so it survives refreshes.
  const [isNavExpanded, setIsNavExpanded] = useState(() => {
    try { return localStorage.getItem('banyak:nav_expanded') === 'true'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('banyak:nav_expanded', String(isNavExpanded)); } catch {}
  }, [isNavExpanded]);

  // Reminder dropdown in the header toolbar. `showReminderMenu` gates
  // the dropdown; `reminderCustomDays` is the custom input; `reminderSaving`
  // guards double-submits; `reminderMessage` shows a transient success
  // string ("Reminder set for Nov 15, 2026") after a successful POST.
  //
  // Menu is portaled to document.body because the preview header has
  // `overflow-hidden` — an absolute-positioned dropdown gets clipped.
  // We track the button's bounding rect so the portal positions itself
  // directly beneath the icon.
  const [showReminderMenu, setShowReminderMenu] = useState(false);
  const [reminderCustomDays, setReminderCustomDays] = useState('');
  const [reminderSaving, setReminderSaving] = useState(false);
  const [reminderMessage, setReminderMessage] = useState('');
  // Optional free-text note attached to the reminder so the recruiter
  // has context when it fires later ("check hiring signal", "he was
  // considering the Blackrock offer", etc.). Cleared on successful send.
  const [reminderNote, setReminderNote] = useState('');
  const reminderBtnRef = useRef(null);
  const [reminderMenuPos, setReminderMenuPos] = useState({ top: 0, right: 0 });

  // Outreach left sidebar — internals moved to AddToCampaignSidebar
  // (campaign list, search, sort, customizer, contact picker all live
  // inside the shared component now). This one boolean gates whether
  // the sidebar renders as the active left panel; open handlers for
  // sibling panels close it, and its own onClose handler resets it.
  const [showOutreachPanel, setShowOutreachPanel] = useState(false);
  const [showMessagingPanel, setShowMessagingPanel] = useState(false);
  // AI profile suggestions panel — mutually exclusive with the other
  // left panels, same as outreach/presentation/feedback/opportunities.
  const [showSuggestionsPanel, setShowSuggestionsPanel] = useState(false);
  // Pending-suggestion count drives the nav badge. Seeded by a light fetch on
  // candidate change so the badge shows before the panel is ever opened, then
  // kept live by the panel via onCountChange while it's open.
  const [suggestionCount, setSuggestionCount] = useState(0);

  // Candidate code editing
  const [editingCode, setEditingCode] = useState(false);
  const [codeValue, setCodeValue] = useState('');
  
  // Stage change dropdown
  const [pipelineStages, setPipelineStages] = useState([]);

  // Pipeline context
  const [activeCardId, setActiveCardId] = useState(cardId || null);
  const [activePipelineId, setActivePipelineId] = useState(pipelineId || null);
  const [activePipelineName, setActivePipelineName] = useState(pipelineName || '');
  const [candidateSearches, setCandidateSearches] = useState([]);
  const [rating, setRating] = useState(0);
  const [status, setStatus] = useState('pending');
  const [showRejectReason, setShowRejectReason] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [showWithdrawReason, setShowWithdrawReason] = useState(false);
  const [withdrawReason, setWithdrawReason] = useState('');
  const [savingRating, setSavingRating] = useState(false);
  const [hoverRating, setHoverRating] = useState(0);
  const [interviewFeedback, setInterviewFeedback] = useState([]);
  const isPipelineContext = !!(activeCardId && activePipelineId);

  // Feedback form state
  const [allStages, setAllStages] = useState([]);
  const [newFeedback, setNewFeedback] = useState({ stage_id: '', interviewer_name: '', feedback_text: '', rating: 0, meeting_id: '' });
  const [savingFeedback, setSavingFeedback] = useState(false);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [cardMeetings, setCardMeetings] = useState([]);

  // Card details state
  const [cardData, setCardData] = useState(null);
  const [presentationTab, setPresentationTab] = useState('presentation-files');
  const [showAssignModal, setShowAssignModal] = useState(false); // REMOVABLE: replaced by inline AssignPills
  const [updatingPriority, setUpdatingPriority] = useState(false);
  const [showPriorityMenu, setShowPriorityMenu] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // Search pools the candidate is in but not yet promoted to a pipeline card.
  // The real flow is add-to-pool then upgrade, and this panel is where
  // pipeline-related actions live, so the upgrade belongs here.
  const [upgradablePools, setUpgradablePools] = useState([]);
  const [upgradingPoolId, setUpgradingPoolId] = useState(null);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  // Interviews panel — mirrors the Introductions search-dropdown + inline code editor.
  const [showFeedbackSearchDropdown, setShowFeedbackSearchDropdown] = useState(false);
  const [editingFeedbackCode, setEditingFeedbackCode] = useState(false);
  const [feedbackCodeValue, setFeedbackCodeValue] = useState('');
  const [showReviewActions, setShowReviewActions] = useState(false);

  // Presentation files state
  const [presentationFiles, setPresentationFiles] = useState([]);
  const [uploadingPresFile, setUploadingPresFile] = useState(false);
  const presFileInputRef = useRef(null);
  const presFileInputRef2 = useRef(null);

  const reorderPresFile = async (index, direction) => {
    const newFiles = [...presentationFiles];
    const swapIdx = index + direction;
    if (swapIdx < 0 || swapIdx >= newFiles.length) return;
    [newFiles[index], newFiles[swapIdx]] = [newFiles[swapIdx], newFiles[index]];
    setPresentationFiles(newFiles);
    // Save order to backend
    try {
      const order = newFiles.map((f, i) => ({ id: f.id, sort_order: i }));
      await fetch(`/api/pipelines/${activePipelineId}/presentation-files/reorder`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ order })
      });
    } catch (e) { console.error('Reorder failed:', e); }
  };

  const renderPresentationFilesList = (inputRef) => (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-gray-700">Presentation Files</p>
        {!isClientUser && hasPermission('upload_files') && (
          <button onClick={() => inputRef?.current?.click()} disabled={uploadingPresFile}
            className="text-xs px-2.5 py-1 bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50">
            {uploadingPresFile ? 'Uploading...' : '+ Upload'}
          </button>
        )}
      </div>
      <input ref={inputRef} type="file" className="hidden" onChange={handleUploadPresFile}
        accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.jpg,.jpeg,.png,.gif" />
      {presentationFiles.length === 0 ? (
        <div className="text-center py-8">
          <svg className="w-7 h-7 text-gray-300 mx-auto mb-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
          <p className="text-xs text-gray-400">No presentation files yet</p>
          {!isClientUser && <p className="text-[10px] text-gray-400 mt-1">Upload files to share with the client</p>}
        </div>
      ) : (
        <div className="space-y-1.5">
          {presentationFiles.map((f, idx) => (
            <div key={f.id} className="flex items-center gap-2 group border rounded-lg p-2 hover:border-emerald-300 hover:bg-emerald-50/50 transition-colors">
              {/* Order number + arrows */}
              {!isClientUser && presentationFiles.length > 1 && (
                <div className="flex flex-col items-center flex-shrink-0 w-5">
                  <button onClick={() => reorderPresFile(idx, -1)} disabled={idx === 0}
                    className={`text-gray-300 hover:text-gray-600 ${idx === 0 ? 'invisible' : ''}`}>
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" /></svg>
                  </button>
                  <span className="text-[9px] font-bold text-gray-400">{idx + 1}</span>
                  <button onClick={() => reorderPresFile(idx, 1)} disabled={idx === presentationFiles.length - 1}
                    className={`text-gray-300 hover:text-gray-600 ${idx === presentationFiles.length - 1 ? 'invisible' : ''}`}>
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                  </button>
                </div>
              )}
              <div className="w-7 h-7 rounded bg-emerald-100 flex items-center justify-center flex-shrink-0">
                <svg className="w-3.5 h-3.5 text-emerald-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <button onClick={() => openPresFile(f.id, f.filename)}
                  className="text-xs font-medium text-gray-800 hover:text-emerald-700 hover:underline truncate block text-left w-full">{f.filename}</button>
                <p className="text-[10px] text-gray-400">{f.uploaded_by_name ? `${f.uploaded_by_name}` : ''}{f.uploaded_at ? ` · ${new Date(f.uploaded_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : ''}</p>
              </div>
              <button onClick={() => openPresFile(f.id, f.filename)}
                className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-emerald-700 transition-opacity flex-shrink-0" title="Open in new tab">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </button>
              <button onClick={() => downloadPresFile(f.id, f.filename)}
                className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-green-600 transition-opacity flex-shrink-0" title="Download">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
              </button>
              {!isClientUser && (
                <button onClick={() => handleDeletePresFile(f.id)}
                  className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-red-500 transition-opacity flex-shrink-0" title="Delete">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // Opportunities state
  const [oppRoles, setOppRoles] = useState([]);
  const [oppRolesLoading, setOppRolesLoading] = useState(false);
  const [candidateRoleIds, setCandidateRoleIds] = useState(new Set());
  const [togglingRole, setTogglingRole] = useState(null);

  // Searches state (for opportunities panel)
  const [oppSearches, setOppSearches] = useState([]);
  const [candidateSearchIds, setCandidateSearchIds] = useState(new Set()); // pipeline IDs candidate is already in

  const interviewRatings = interviewFeedback.filter(f => f.rating > 0).map(f => Number(f.rating));
  const parsedRating = Number(rating) || 0;
  const allRatings = parsedRating > 0 ? [parsedRating, ...interviewRatings] : interviewRatings;
  const overallAverage = allRatings.length > 0 ? Math.round((allRatings.reduce((a, b) => a + b, 0) / allRatings.length) * 10) / 10 : 0;
  const displayStars = Math.round(overallAverage);
  const MIN_MAIN = 400; const MIN_SIDE = 280; const MAX_SIDE = 560; const MAX_PCT = 0.95;
  const readOnly = userType !== 'client';
  const isClientUser = userType === 'client';
  const widthRef = useRef(width); const fbWidthRef = useRef(feedbackWidth); const cdWidthRef = useRef(presentationWidth); const notesWidthRef = useRef(notesWidth);
  useEffect(() => { widthRef.current = width; }, [width]);
  useEffect(() => { fbWidthRef.current = feedbackWidth; }, [feedbackWidth]);
  useEffect(() => { cdWidthRef.current = presentationWidth; }, [presentationWidth]);
  useEffect(() => { notesWidthRef.current = notesWidth; }, [notesWidth]);

  // Which left panel is active (only one at a time)
  const activeLeftPanel = showSuggestionsPanel ? 'suggestions' : (showOutreachPanel ? 'outreach' : (showMessagingPanel ? 'messaging' : (isPresentationOpen ? 'presentation' : (isFeedbackPanelOpen && isPipelineContext ? 'feedback' : (isOpportunitiesOpen ? 'opportunities' : null)))));
  const leftPanelWidth = activeLeftPanel === 'presentation' ? presentationWidth : (activeLeftPanel === 'feedback' ? feedbackWidth : (activeLeftPanel === 'opportunities' ? presentationWidth : ((activeLeftPanel === 'outreach' || activeLeftPanel === 'suggestions' || activeLeftPanel === 'messaging') ? presentationWidth : 0)));

  useEffect(() => { if (candidateId && isOpen) { loadProfile(); loadSearches(); } }, [candidateId, isOpen, refreshTrigger]);
  useEffect(() => { if (isFeedbackPanelOpen && activePipelineId) loadFeedbackData(); }, [isFeedbackPanelOpen, activePipelineId, activeCardId]);
  useEffect(() => { if ((isPresentationOpen || isFeedbackPanelOpen) && activeCardId) loadCardData(); }, [isPresentationOpen, isFeedbackPanelOpen, activeCardId]);
  useEffect(() => { if (activeCardId) loadPresentationFiles(activeCardId); }, [activeCardId]);
  useEffect(() => { if (isPresentationOpen) { loadUpgradablePools(); } }, [isPresentationOpen, candidateId]);

  // Load the internal stages for the timeline as soon as we have an active
  // search (previously stages loaded only when the stage dropdown opened).
  useEffect(() => {
    if (!isPresentationOpen || !activePipelineId) return;
    if (pipelineStages.length > 0 && pipelineStages._pipelineId === activePipelineId) return;
    (async () => {
      try {
        const res = await fetch(`/api/pipelines/${activePipelineId}/detailed`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        const stages = (data.stages || []).map(st => ({ id: st.id, name: st.name, stage_type: st.stage_type }));
        stages._pipelineId = activePipelineId;
        setPipelineStages(stages);
      } catch (e) { console.error(e); }
    })();
  }, [isPresentationOpen, activePipelineId]);

  // Timeline branch action: Disqualified/Withdrew both take a reason and both
  // move the card to Disqualified server-side (handleUpdateStatus does that).
  // Reuse the existing reason flows rather than inventing a new one.
  const handleTimelineBranch = (branch) => {
    if (branch === 'withdrew') { setShowWithdrawReason(true); setShowRejectReason(false); }
    else { setShowRejectReason(true); setShowWithdrawReason(false); }
  };
  useEffect(() => { if (isOpportunitiesOpen && candidateId) loadOpportunities(); }, [isOpportunitiesOpen, candidateId]);

  // Refresh the suggestion badge without opening the panel. Recruiter/admin
  // only — clients don't have the feature. A 404 (server half not deployed),
  // non-JSON, or any error just leaves the count at 0, so the badge silently
  // stays hidden.
  const refreshSuggestionCount = useCallback(async () => {
    if (isClientUser || !candidateId) { setSuggestionCount(0); return; }
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`/api/candidates/${candidateId}/suggestions?status=pending`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) { setSuggestionCount(0); return; }
      const ct = res.headers.get('content-type') || '';
      if (!ct.includes('application/json')) { setSuggestionCount(0); return; }
      const list = await res.json();
      setSuggestionCount(Array.isArray(list) ? list.length : 0);
    } catch {
      setSuggestionCount(0);
    }
  }, [candidateId, isClientUser]);

  // Seed on candidate change.
  useEffect(() => { refreshSuggestionCount(); }, [refreshSuggestionCount]);

  // When a note is saved the server starts a scan that finishes a few seconds
  // later, so an immediate recount would miss it. Recount shortly after, and
  // again a little later, to catch the scan landing without needing the panel
  // open. Cheap GETs, and they no-op to the same count if nothing changed.
  const handleNoteSaved = useCallback(() => {
    refreshSuggestionCount();
    const t1 = setTimeout(refreshSuggestionCount, 4000);
    const t2 = setTimeout(refreshSuggestionCount, 9000);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [refreshSuggestionCount]);

  const loadProfile = async () => { try { setLoading(true); setProfile(await clientCandidateDataService.getCompleteProfile(candidateId)); } catch (e) { console.error(e); } finally { setLoading(false); } };
  const loadFeedback = async (cId) => { if (!cId) return; try { setInterviewFeedback(await clientService.getStageFeedback(cId) || []); } catch { setInterviewFeedback([]); } };
  const loadFeedbackData = async () => {
    if (!activePipelineId || !activeCardId) return;
    try {
      setFeedbackLoading(true);
      const token = localStorage.getItem('authToken');
      const [s, f, mResp] = await Promise.all([
        clientService.getInterviewStages(activePipelineId),
        clientService.getStageFeedback(activeCardId),
        fetch(`/api/calendar/meetings/${activeCardId}`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setAllStages(s);
      setInterviewFeedback(f || []);
      if (mResp.ok) setCardMeetings(await mResp.json());
    }
    catch (e) { console.error(e); } finally { setFeedbackLoading(false); }
  };
  const loadCardData = async () => { if (!activeCardId) return; try { const data = await pipelineService.getCard(activeCardId); setCardData(data); } catch (e) { console.error(e); } };
  const loadPresentationFiles = async (cId) => {
    if (!cId) return;
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`/api/cards/${cId}/presentation-files`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const files = await res.json();
        files.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
        setPresentationFiles(files);
      }
    } catch { setPresentationFiles([]); }
  };
  const handleUploadPresFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !activeCardId) return;
    setUploadingPresFile(true);
    try {
      const token = localStorage.getItem('authToken');
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`/api/cards/${activeCardId}/presentation-files`, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd });
      if (res.ok) await loadPresentationFiles(activeCardId);
    } catch (err) { console.error('Upload error:', err); }
    finally { setUploadingPresFile(false); if (presFileInputRef.current) presFileInputRef.current.value = ''; }
  };
  const handleDeletePresFile = async (docId) => {
    if (!window.confirm('Delete this presentation file?')) return;
    try {
      const token = localStorage.getItem('authToken');
      await fetch(`/api/cards/presentation-files/${docId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      setPresentationFiles(prev => prev.filter(f => f.id !== docId));
    } catch {}
  };
  const openPresFile = async (docId, filename) => {
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`/api/cards/presentation-files/${docId}/download`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) { console.error('Error opening file:', err); alert('Failed to open file'); }
  };

  const downloadPresFile = async (docId, filename) => {
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`/api/cards/presentation-files/${docId}/download`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) { console.error('Error downloading file:', err); alert('Failed to download file'); }
  };
  const loadSearches = async () => {
    const token = localStorage.getItem('authToken'); const headers = { Authorization: `Bearer ${token}` };
    try { const res = await fetch(`/api/client/candidates/${candidateId}/pipelines`, { headers }); if (res.ok) { const rows = await res.json(); const searches = rows.filter(r => r.category !== 'project').map(r => ({ card_id: r.card_id, pipeline_id: r.pipeline_id, pipeline_name: r.pipeline_name, firm_name: r.firm_name, rating: r.rating || 0, status: r.candidate_status || 'pending' })); if (searches.length > 0) { setCandidateSearches(searches); selectActiveSearch(searches); return; } } } catch {}
    if (pipelineId && cardId) {
      try { const res = await fetch(`/api/pipelines/${pipelineId}/client-stages`, { headers }); if (res.ok) { const data = await res.json(); const all = [...(data.unassigned || []), ...(data.stages || []).flatMap(s => s.candidates)]; const m = all.find(c => c.card_id === cardId || c.card_id === parseInt(cardId)); const e = { card_id: parseInt(cardId), pipeline_id: parseInt(pipelineId), pipeline_name: pipelineName || 'Current Search', rating: m?.rating || 0, status: m?.status || 'pending' }; setCandidateSearches([e]); setActiveCardId(e.card_id); setActivePipelineId(e.pipeline_id); setActivePipelineName(e.pipeline_name); setRating(e.rating); setStatus(e.status); loadFeedback(e.card_id); return; } } catch {}
      const e = { card_id: parseInt(cardId), pipeline_id: parseInt(pipelineId), pipeline_name: pipelineName || 'Search', rating: 0, status: 'pending' }; setCandidateSearches([e]); setActiveCardId(e.card_id); setActivePipelineId(e.pipeline_id); setActivePipelineName(e.pipeline_name); loadFeedback(e.card_id);
    }
  };

  // Pools awaiting upgrade to a pipeline. Guarded like the other loaders:
  // any error or non-JSON (route not deployed) just leaves the list empty,
  // so the section silently hides rather than erroring.
  const loadUpgradablePools = async () => {
    if (!candidateId) { setUpgradablePools([]); return; }
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`/api/pipelines/candidate/${candidateId}/search-pools`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) { setUpgradablePools([]); return; }
      const ct = res.headers.get('content-type') || '';
      if (!ct.includes('application/json')) { setUpgradablePools([]); return; }
      setUpgradablePools(await res.json());
    } catch { setUpgradablePools([]); }
  };

  const handleUpgradePool = async (targetPipelineId) => {
    if (!candidateId || !targetPipelineId) return;
    setUpgradingPoolId(targetPipelineId);
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`/api/pipelines/${targetPipelineId}/search-candidates/to-pipeline`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ candidate_ids: [candidateId] }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert(err.error || 'Failed to upgrade to pipeline');
        return;
      }
      const result = await res.json().catch(() => ({}));
      // Drop it from the upgrade list.
      await loadUpgradablePools();
      // Add the upgraded search to the selector directly and switch to it.
      // We can't rely on loadSearches() here because the candidate-pipelines
      // endpoint is firm-scoped and won't return a pipeline owned by another
      // firm — which is exactly the case that made the candidate "disappear."
      const newCard = result.cards?.find(c => String(c.candidate_id) === String(candidateId)) || result.cards?.[0];
      if (newCard?.card_id) {
        const entry = {
          card_id: newCard.card_id,
          pipeline_id: result.pipeline_id || targetPipelineId,
          pipeline_name: result.pipeline_name || 'Search',
          rating: 0,
          status: 'pending',
        };
        setCandidateSearches(prev => {
          const without = prev.filter(s => s.card_id !== entry.card_id);
          return [...without, entry];
        });
        handleSwitchSearch(entry);
      } else {
        // Fallback: best-effort reload if the endpoint didn't return a card.
        await loadSearches();
      }
    } catch (e) {
      console.error('Upgrade failed:', e);
      alert('Failed to upgrade to pipeline');
    } finally {
      setUpgradingPoolId(null);
    }
  };
  const selectActiveSearch = (searches) => { const m = pipelineId ? searches.find(s => s.pipeline_id === pipelineId || s.pipeline_id === parseInt(pipelineId)) : null; const sel = m || searches[0]; setActiveCardId(sel.card_id); setActivePipelineId(sel.pipeline_id); setActivePipelineName(sel.pipeline_name); setRating(sel.rating); setStatus(sel.status); loadFeedback(sel.card_id); };
  const handleSwitchSearch = (s) => { setActiveCardId(s.card_id); setActivePipelineId(s.pipeline_id); setActivePipelineName(s.pipeline_name); setRating(s.rating || 0); setStatus(s.status || 'pending'); loadFeedback(s.card_id); };
  const updateSearchState = (f, v) => { setCandidateSearches(prev => prev.map(s => s.card_id === activeCardId ? { ...s, [f]: v } : s)); };
  const handleSaveRating = async (nr) => { if (!activeCardId || nr === 0) return; setSavingRating(true); try { await clientService.rateCandidate(activeCardId, nr, ''); setRating(nr); updateSearchState('rating', nr); } catch {} finally { setSavingRating(false); } };
  const handleUpdateStatus = async (ns, reason) => { if (!activeCardId) return; try { await clientService.updateCandidateStatus(activeCardId, ns, reason); setStatus(ns); updateSearchState('status', ns); setShowRejectReason(false); setRejectReason(''); setShowWithdrawReason(false); setWithdrawReason(''); } catch {} };
  const handleSubmitFeedback = async () => { if (!newFeedback.stage_id || !newFeedback.feedback_text.trim()) { alert('Select a stage and provide feedback'); return; } setSavingFeedback(true); try { await clientService.addStageFeedback(activeCardId, newFeedback.stage_id, newFeedback.feedback_text, newFeedback.interviewer_name, newFeedback.rating, newFeedback.meeting_id || null); setNewFeedback({ stage_id: '', interviewer_name: '', feedback_text: '', rating: 0, meeting_id: '' }); await loadFeedbackData(); } catch { alert('Failed'); } finally { setSavingFeedback(false); } };

  // Card details actions
  const handlePriorityChange = async (p) => { if (!cardData || p === cardData.priority) return; setUpdatingPriority(true); try { await pipelineService.updateCardPriority(activeCardId, p); setCardData({ ...cardData, priority: p }); } catch {} finally { setUpdatingPriority(false); } };
  const handleDeleteCard = async () => { if (!cardData || !window.confirm(`Remove ${cardData.name} from this pipeline?`)) return; setDeleting(true); try { await pipelineService.deleteCard(activeCardId); onCardDelete?.(); onClose(); } catch { setDeleting(false); } };

  // Opportunities functions
  const loadOpportunities = async () => {
    const headers = { Authorization: `Bearer ${localStorage.getItem('authToken')}` };
    try {
      setOppRolesLoading(true);
      const [rolesRes, searchesRes, candPipelinesRes] = await Promise.all([
        fetch('/api/opportunity-roles', { headers }),
        fetch('/api/pipelines/all-searches', { headers }),
        fetch(`/api/client/candidates/${candidateId}/pipelines`, { headers }).catch(() => null)
      ]);

      // Load roles and check which ones this candidate is in
      if (rolesRes.ok) {
        const allRoles = await rolesRes.json();
        setOppRoles(allRoles);
        const linkedIds = new Set();
        for (const role of allRoles) {
          try {
            const cRes = await fetch(`/api/opportunity-roles/${role.id}/candidates`, { headers });
            if (cRes.ok) {
              const cands = await cRes.json();
              if (cands.some(c => c.candidate_id === candidateId || c.candidate_id === parseInt(candidateId))) {
                linkedIds.add(role.id);
              }
            }
          } catch {}
        }
        setCandidateRoleIds(linkedIds);
      }

      // Load executive searches (not projects)
      if (searchesRes?.ok) {
        const allSearches = await searchesRes.json();
        setOppSearches(allSearches.filter(s => s.category === 'executive_search' && s.status === 'active'));
      }

      // Check which pipelines this candidate is already in
      if (candPipelinesRes?.ok) {
        const rows = await candPipelinesRes.json();
        setCandidateSearchIds(new Set(rows.map(r => r.pipeline_id)));
      }
    } catch (err) { console.error('Error loading opportunities:', err); }
    finally { setOppRolesLoading(false); }
  };

  const toggleCandidateRole = async (roleId) => {
    const headers = { Authorization: `Bearer ${localStorage.getItem('authToken')}`, 'Content-Type': 'application/json' };
    setTogglingRole(roleId);
    try {
      if (candidateRoleIds.has(roleId)) {
        await fetch(`/api/opportunity-roles/${roleId}/candidates/${candidateId}`, { method: 'DELETE', headers });
        setCandidateRoleIds(prev => { const n = new Set(prev); n.delete(roleId); return n; });
      } else {
        await fetch(`/api/opportunity-roles/${roleId}/candidates`, { method: 'POST', headers, body: JSON.stringify({ candidate_id: candidateId }) });
        setCandidateRoleIds(prev => new Set([...prev, roleId]));
      }
    } catch (err) { alert('Failed to update'); }
    finally { setTogglingRole(null); }
  };

  const updateCandidateRoleStatus = async (roleId, status) => {
    const headers = { Authorization: `Bearer ${localStorage.getItem('authToken')}`, 'Content-Type': 'application/json' };
    try {
      await fetch(`/api/opportunity-roles/${roleId}/candidates/${candidateId}`, { method: 'PUT', headers, body: JSON.stringify({ status }) });
    } catch (err) { alert('Failed to update status'); }
  };

  const handleSyncLinkedIn = async () => {
    if (linkedinSyncing) return;

    const linkedinUrl = profile?.linkedin_url || profile?.linkedin;
    if (!linkedinUrl) {
      alert('No LinkedIn URL set for this candidate.\n\nAdd a LinkedIn URL in the contacts section first.');
      return;
    }

    if (!window.confirm('Sync this candidate from LinkedIn?\n\nFills blank fields and adds new employment/education entries. Existing data is preserved.')) {
      return;
    }

    const token = localStorage.getItem('authToken');
    setLinkedinSyncing(true);
    setLinkedinSyncStatus('Triggering...');

    try {
      // Step 1: trigger
      const triggerRes = await fetch(`/api/candidates/${candidateId}/sync-linkedin-trigger`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });
      const triggerData = await triggerRes.json();
      if (!triggerRes.ok) {
        throw new Error(triggerData.detail || triggerData.error || 'Trigger failed');
      }

      const snapshotId = triggerData.snapshot_id;
      setLinkedinSyncStatus('Scraping LinkedIn...');

      // Step 2: poll for completion (max 60 × 5s = 5 min)
      let attempts = 0;
      let result = null;
      while (attempts < 60) {
        await new Promise(r => setTimeout(r, 5000));
        attempts++;

        const statusRes = await fetch(
          `/api/candidates/${candidateId}/sync-linkedin-status?snapshot_id=${snapshotId}`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        const statusData = await statusRes.json();

        if (statusData.ready) {
          result = statusData;
          break;
        }
        if (statusData.status === 'failed') {
          throw new Error(statusData.error || 'Sync failed on LinkedIn side');
        }

        setLinkedinSyncStatus(`Scraping... (${attempts * 5}s)`);
      }

      if (!result) {
        throw new Error('Sync timed out after 5 minutes — try again later');
      }

      if (result.empty) {
        alert('LinkedIn returned no useful data for this profile.\n\nThe profile may be private, deleted, or have no public information.');
      } else {
        const synced = result.synced || {};
        const summary = [];
        if (synced.name) summary.push('name');
        if (synced.headline) summary.push('headline');
        if (synced.summary) summary.push('summary');
        if (synced.location) summary.push('location');
        if (synced.photo) summary.push('photo');
        if (synced.followers) summary.push('followers');
        if (synced.connections) summary.push('connections');
        if (synced.employment_added > 0) summary.push(`${synced.employment_added} job(s)`);
        if (synced.education_added > 0) summary.push(`${synced.education_added} education entr${synced.education_added > 1 ? 'ies' : 'y'}`);

        // Detect sparse-data case: got SOME profile metadata but NO employment/education
        // This usually means the LinkedIn profile has limited public visibility
        const ld = result.linkedin_data || {};
        const sparseProfile = (synced.employment_added === 0 && synced.education_added === 0 && 
                                ld.experience_count === 0 && ld.education_count === 0);

        let firmSummary = '';
        if (synced.firms_created > 0 || synced.firms_linked > 0) {
          const parts = [];
          if (synced.firms_created > 0) parts.push(`${synced.firms_created} new firm${synced.firms_created > 1 ? 's' : ''} created`);
          if (synced.firms_linked > 0) parts.push(`${synced.firms_linked} linked to existing`);
          firmSummary = `\n\nFirms: ${parts.join(', ')}`;
        }
        
        let dupWarning = '';
        if (synced.firms_possible_duplicates > 0) {
          dupWarning = `\n\n⚠️ ${synced.firms_possible_duplicates} possible firm duplicate${synced.firms_possible_duplicates > 1 ? 's' : ''} detected.\nGo to Firms → Deduplicate to review.`;
        }

        let sparseWarning = '';
        if (sparseProfile) {
          sparseWarning = `\n\n⚠️ Note: LinkedIn returned only basic profile data (no employment or education history). This usually means the profile has restricted visibility or the candidate's public profile is sparse. Other fields above were updated successfully.`;
        }

        alert(`✓ LinkedIn sync complete!\n\nUpdated: ${summary.join(', ') || '(no changes — data already current)'}${firmSummary}${dupWarning}${sparseWarning}`);
      }

      handleRefresh();
    } catch (err) {
      if (err.message?.includes('Sync cooldown') || err.message?.toLowerCase().includes('cooldown')) {
        alert(`Cooldown active: ${err.message}\n\nAdmins can change the cooldown in User Management → Settings.`);
      } else {
        alert('LinkedIn sync failed: ' + err.message);
      }
    } finally {
      setLinkedinSyncing(false);
      setLinkedinSyncStatus('');
    }
  };

  const handleRefresh = () => setRefreshTrigger(p => p + 1);

  // Campaign enrollment — sidebar delegated to AddToCampaignSidebar.
  // This component only owns whether the sidebar is open and the
  // terminal callback. All the multi-step logic (pick → customize →
  // contacts) lives in the shared sidebar component.
  const openOutreachPanel = () => {
    if (showOutreachPanel) { setShowOutreachPanel(false); return; }
    // Close other left panels — mutually exclusive with presentation/feedback/
    // opportunities AND suggestions AND messaging (all left-panel occupants).
    setIsPresentationOpen(false);
    setIsFeedbackPanelOpen(false);
    setIsOpportunitiesOpen(false);
    setShowSuggestionsPanel(false);
    setShowMessagingPanel(false);
    setShowOutreachPanel(true);
  };

  const closeOutreachPanel = () => setShowOutreachPanel(false);

  // Messaging panel — the candidate's own SMS reply thread (the /outreach
  // Replies tab scoped to just this candidate).
  const openMessagingPanel = () => {
    if (showMessagingPanel) { setShowMessagingPanel(false); return; }
    setIsPresentationOpen(false);
    setIsFeedbackPanelOpen(false);
    setIsOpportunitiesOpen(false);
    setShowSuggestionsPanel(false);
    setShowOutreachPanel(false);
    setShowMessagingPanel(true);
  };

  const closeMessagingPanel = () => setShowMessagingPanel(false);

  // Suggestions panel — reads notes/transcripts and proposes widget updates.
  const openSuggestionsPanel = () => {
    if (showSuggestionsPanel) { setShowSuggestionsPanel(false); return; }
    setIsPresentationOpen(false);
    setIsFeedbackPanelOpen(false);
    setIsOpportunitiesOpen(false);
    setShowOutreachPanel(false);
    setShowMessagingPanel(false);
    setShowSuggestionsPanel(true);
  };

  const closeSuggestionsPanel = () => setShowSuggestionsPanel(false);

  // Create a reminder for the currently-open candidate. Posts to
  // /api/reminders with days_from_now; server computes remind_at.
  // Shows a transient success message inside the dropdown and closes
  // it after ~1.5s so the user can start another action.
  const createReminder = async (days) => {
    if (!candidateId || reminderSaving) return;
    setReminderSaving(true);
    setReminderMessage('');
    try {
      const res = await fetch(`/api/reminders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('authToken')}` },
        body: JSON.stringify({ candidate_id: candidateId, days_from_now: days, note: reminderNote.trim() || undefined }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'Could not set reminder');
      }
      const created = await res.json();
      const when = new Date(created.remind_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      setReminderMessage(`✓ Reminder set for ${when}`);
      setReminderCustomDays('');
      setReminderNote('');
      // Auto-close after showing success briefly
      setTimeout(() => { setShowReminderMenu(false); setReminderMessage(''); }, 1500);
    } catch (e) {
      console.error('[banyak-reminder] create failed', e);
      setReminderMessage(e.message || 'Failed to set reminder');
    } finally {
      setReminderSaving(false);
    }
  };

  const handleEnrolled = (result) => {
    setShowOutreachPanel(false);
    if (result?.enrolled > 0) alert('Enrolled in campaign');
    else alert('Already enrolled in this campaign');
  };

  const saveCode = async () => {
    if (!activeCardId) return;
    try {
      await fetch(`/api/cards/${activeCardId}/code`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ candidate_code: codeValue.trim() || null })
      });
      setCardData(prev => ({ ...prev, candidate_code: codeValue.trim() || null }));
      setEditingCode(false);
    } catch (e) { console.error(e); }
  };

  const saveFeedbackCode = async () => {
    if (!activeCardId) return;
    try {
      await fetch(`/api/cards/${activeCardId}/code`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ candidate_code: feedbackCodeValue.trim() || null })
      });
      setCardData(prev => ({ ...prev, candidate_code: feedbackCodeValue.trim() || null }));
      setEditingFeedbackCode(false);
    } catch (e) { console.error(e); }
  };

  const startCall = async () => {
    // Phones are at profile.client_data.phones (array of {value, type, is_primary})
    let phones = [];
    try {
      const ccdPhones = profile?.client_data?.phones;
      if (Array.isArray(ccdPhones)) phones = ccdPhones.filter(p => p.value);
      // Fallback: fetch directly from profile endpoint
      if (phones.length === 0) {
        const res = await fetch(`/api/client/candidates/${candidateId}/profile`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` }
        });
        if (res.ok) {
          const data = await res.json();
          const fetched = data?.client_data?.phones || [];
          if (Array.isArray(fetched)) phones = fetched.filter(p => p.value);
        }
      }
    } catch (e) {}
    
    setCandidatePhones(phones);
    if (phones.length === 1) {
      setCallPhoneNumber(phones[0].value);
      setShowCallWidget(true);
    } else if (phones.length > 1) {
      setShowPhonePicker(true);
    } else {
      setCallPhoneNumber('');
      setShowCallWidget(true);
    }
  };

  const selectPhoneAndCall = (phone) => {
    setCallPhoneNumber(phone);
    setShowPhonePicker(false);
    setShowCallWidget(true);
  };

  const changeStage = async (stageId, stageName) => {
    try {
      await pipelineService.moveCard(activeCardId, stageId);
      setCardData(prev => ({ ...prev, stage_id: stageId, stage_name: stageName }));
      setShowStageDropdown(false);
    } catch (e) { console.error(e); }
  };

  const handleEditModeChange = (s) => setEditModeState(s);

  // Resize
  // ─── Resize logic ────────────────────────────────────────────────────────
  // Two resizable dimensions:
  //   • width      — main candidate panel; controlled by the LEFTMOST handle
  //                  (regardless of whether a fixed-width sidebar is open).
  //   • notesWidth — Notes panel; controlled by the handle BETWEEN main and notes.
  // Sidebars (feedback / presentation / opportunities) are FIXED width
  // (SIDEBAR_FIXED_WIDTH) and not user-resizable.
  useEffect(() => {
    if (!resizingTarget) return;
    const handleMove = (e) => {
      const maxTotal = window.innerWidth * MAX_PCT;
      const sidebarW = activeLeftPanel ? SIDEBAR_FIXED_WIDTH : 0;
      const notesW = isNotesPanelOpen ? notesWidthRef.current : 0;
      const handles = (1 /* outer */ + (activeLeftPanel ? 1 : 0) + (isNotesPanelOpen ? 1 : 0)) * 8;

      if (resizingTarget === 'side' || resizingTarget === 'main') {
        // Leftmost handle (left edge of the overlay). Always sizes MAIN only.
        // Mouse position determines the overlay's left edge.
        const desiredTotal = Math.min(maxTotal, window.innerWidth - e.clientX);
        const desiredMain = desiredTotal - sidebarW - notesW - handles;
        const newMain = Math.max(MIN_MAIN, Math.min(maxTotal - sidebarW - notesW - handles, desiredMain));
        setWidth(newMain);

      } else if (resizingTarget === 'notes') {
        // Handle between main and notes. Notes grows by stealing from main.
        const desiredNotes = Math.max(300, Math.min(Math.floor(window.innerWidth * 0.75), window.innerWidth - e.clientX));
        const diff = desiredNotes - notesW;
        if (diff > 0) {
          // Growing notes — steal from main first
          const give = Math.min(diff, widthRef.current - MIN_MAIN);
          if (give > 0) { setWidth(widthRef.current - give); setNotesWidth(notesW + give); }
        } else {
          // Shrinking notes — give back to main
          setNotesWidth(desiredNotes);
          setWidth(widthRef.current + (notesW - desiredNotes));
        }
      }
    };
    const handleUp = () => {
      setResizingTarget(null);
      persist(STORAGE_BASES.mainWidth, widthRef.current);
      persist(STORAGE_BASES.notesWidth, notesWidthRef.current);
    };
    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    return () => { document.removeEventListener('mousemove', handleMove); document.removeEventListener('mouseup', handleUp); document.body.style.cursor = ''; document.body.style.userSelect = ''; };
  }, [resizingTarget, activeLeftPanel, isNotesPanelOpen]);

  useEffect(() => { return () => { persist(STORAGE_BASES.mainWidth, widthRef.current); persist(STORAGE_BASES.notesWidth, notesWidthRef.current); }; }, []);
  useEffect(() => {
    const fn = (e) => { if (e.key === 'Escape' && isOpen && !isNotesPanelOpen && !editModeState.isEditMode && !showAssignModal) { if (activeLeftPanel) { setIsFeedbackPanelOpen(false); setIsPresentationOpen(false); setIsOpportunitiesOpen(false); setShowOutreachPanel(false); setShowSuggestionsPanel(false); setShowMessagingPanel(false); } else onClose(); } };
    document.addEventListener('keydown', fn); return () => document.removeEventListener('keydown', fn);
  }, [isOpen, isNotesPanelOpen, activeLeftPanel, onClose, editModeState.isEditMode, showAssignModal]);

  // Broadcast preview open/close so the assistant pill (which lives at
  // the app level, fixed-positioned) can hide itself while the overlay
  // is up. See BottomPill's `previewOverlayOpen` listener.
  useEffect(() => {
    if (!isOpen) return;
    window.dispatchEvent(new CustomEvent('banyak:preview_open'));
    return () => { window.dispatchEvent(new CustomEvent('banyak:preview_closed')); };
  }, [isOpen]);

  // Arrow-key candidate navigation. ↑ = prev, ↓ = next. Only active
  // while the panel is open, and only when the user isn't typing in an
  // input / textarea / contenteditable — otherwise arrow keys inside a
  // note field would flip candidates. Also disabled during Edit Layout
  // and Assign Users modal because those own their own interactions.
  useEffect(() => {
    if (!isOpen) return;
    const fn = (e) => {
      if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
      const t = e.target;
      if (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable) return;
      if (editModeState.isEditMode || showAssignModal) return;
      if (e.key === 'ArrowUp' && hasPrev && onPrevCandidate) {
        e.preventDefault();
        onPrevCandidate();
      } else if (e.key === 'ArrowDown' && hasNext && onNextCandidate) {
        e.preventDefault();
        onNextCandidate();
      }
    };
    document.addEventListener('keydown', fn);
    return () => document.removeEventListener('keydown', fn);
  }, [isOpen, hasPrev, hasNext, onPrevCandidate, onNextCandidate, editModeState.isEditMode, showAssignModal]);

  // "t" hotkey — jumps into the tag search in EnhancedCandidateHeader.
  // Same guards as arrow-key nav (skip while typing, skip in modals /
  // edit-layout mode). Dispatches a window event that
  // EnhancedCandidateHeader listens for and treats as a synthetic
  // "clicked the Add Tag button" — this avoids threading a ref up
  // to CandidatePreviewPanel through prop drilling.
  useEffect(() => {
    if (!isOpen) return;
    const fn = (e) => {
      if (e.key !== 't' && e.key !== 'T') return;
      // Modifier keys (ctrl+t, cmd+t) belong to the browser (new tab), not us
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target;
      if (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable) return;
      if (editModeState.isEditMode || showAssignModal) return;
      e.preventDefault();
      window.dispatchEvent(new CustomEvent('banyak:focus_tag_search'));
    };
    document.addEventListener('keydown', fn);
    return () => document.removeEventListener('keydown', fn);
  }, [isOpen, editModeState.isEditMode, showAssignModal]);

  // Hotkey: 'c' toggles the candidate's clean status. Same guardrails
  // as the 't' hotkey above — modifier keys pass through, typing in
  // inputs is ignored, edit-mode and modals suppress it. Dispatches
  // banyak:toggle_clean; EnhancedCandidateHeader owns the state and
  // reacts to the event with its handleToggleClean handler.
  useEffect(() => {
    if (!isOpen) return;
    const fn = (e) => {
      if (e.key !== 'c' && e.key !== 'C') return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target;
      if (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable) return;
      if (editModeState.isEditMode || showAssignModal) return;
      e.preventDefault();
      window.dispatchEvent(new CustomEvent('banyak:toggle_clean'));
    };
    document.addEventListener('keydown', fn);
    return () => document.removeEventListener('keydown', fn);
  }, [isOpen, editModeState.isEditMode, showAssignModal]);

  const startMainResize = useCallback((e) => { e.preventDefault(); setResizingTarget('main'); }, []);
  const startSideResize = useCallback((e) => { e.preventDefault(); setResizingTarget('side'); }, []);
  const startNotesResize = useCallback((e) => { e.preventDefault(); setResizingTarget('notes'); }, []);

  // ─── Auto-clamp: when a panel opens, shrink main to fit the screen ───────
  useEffect(() => {
    const maxTotal = window.innerWidth * MAX_PCT;
    const sidebarW = activeLeftPanel ? SIDEBAR_FIXED_WIDTH : 0;
    const notesW = isNotesPanelOpen ? notesWidth : 0;
    const handles = 8 + (activeLeftPanel ? 8 : 0) + (isNotesPanelOpen ? 8 : 0);
    // 40px reserved for the new fixed-width vertical nav column
    const rawTotal = width + 40 + sidebarW + notesW + handles;

    if (rawTotal > maxTotal) {
      let overflow = rawTotal - maxTotal;
      let newMain = width;
      let newNotes = notesW;
      // 1. Shrink main first (sidebars are fixed; notes is preserved if possible)
      const mainShrink = Math.min(overflow, newMain - MIN_MAIN);
      if (mainShrink > 0) { newMain -= mainShrink; overflow -= mainShrink; }
      // 2. As a last resort, shrink notes
      if (overflow > 0 && isNotesPanelOpen) {
        const ns = Math.min(overflow, newNotes - 300);
        if (ns > 0) { newNotes -= ns; }
      }
      setWidth(newMain);
      if (isNotesPanelOpen) setNotesWidth(newNotes);
    }
  }, [activeLeftPanel, isNotesPanelOpen]);

  // ─── Restore main width — per-panel snapshots ────────────────────────────
  // Two independent snapshots, one for the sidebars (feedback/presentation/
  // opportunities, which are mutually exclusive) and one for notes. When a
  // panel opens we capture the current main width; when it closes we restore.
  // This way closing notes restores main to the size it had with the sidebar
  // still open, and closing the sidebar later restores it to the original.
  const mainBeforeSidebarRef = useRef(null);
  const mainBeforeNotesRef = useRef(null);
  const prevHasSidebarRef = useRef(false);
  const prevHasNotesRef = useRef(false);
  useEffect(() => {
    const hasSidebar = !!activeLeftPanel;
    const hasNotes = isNotesPanelOpen;

    // Sidebar transitions
    if (!prevHasSidebarRef.current && hasSidebar) {
      mainBeforeSidebarRef.current = widthRef.current;
    } else if (prevHasSidebarRef.current && !hasSidebar) {
      // If notes is still open, propagate the (older = larger) snapshot to notes
      // so closing notes later still restores to the truly original width.
      if (hasNotes && mainBeforeSidebarRef.current != null) {
        if (mainBeforeNotesRef.current == null || mainBeforeSidebarRef.current > mainBeforeNotesRef.current) {
          mainBeforeNotesRef.current = mainBeforeSidebarRef.current;
        }
      }
      const stored = mainBeforeSidebarRef.current;
      if (stored != null && stored > widthRef.current) {
        const maxTotal = window.innerWidth * MAX_PCT;
        const notesW = hasNotes ? notesWidthRef.current : 0;
        const handles = 8 + (hasNotes ? 8 : 0);
        const maxFit = maxTotal - notesW - handles;
        const target = Math.min(stored, maxFit);
        if (target > widthRef.current) setWidth(target);
      }
      mainBeforeSidebarRef.current = null;
    }

    // Notes transitions
    if (!prevHasNotesRef.current && hasNotes) {
      mainBeforeNotesRef.current = widthRef.current;
    } else if (prevHasNotesRef.current && !hasNotes) {
      // If sidebar is still open, propagate the older snapshot back to sidebar
      if (hasSidebar && mainBeforeNotesRef.current != null) {
        if (mainBeforeSidebarRef.current == null || mainBeforeNotesRef.current > mainBeforeSidebarRef.current) {
          mainBeforeSidebarRef.current = mainBeforeNotesRef.current;
        }
      }
      const stored = mainBeforeNotesRef.current;
      if (stored != null && stored > widthRef.current) {
        const maxTotal = window.innerWidth * MAX_PCT;
        const sidebarW = hasSidebar ? SIDEBAR_FIXED_WIDTH : 0;
        const handles = 8 + (hasSidebar ? 8 : 0);
        const maxFit = maxTotal - sidebarW - handles;
        const target = Math.min(stored, maxFit);
        if (target > widthRef.current) setWidth(target);
      }
      mainBeforeNotesRef.current = null;
    }

    prevHasSidebarRef.current = hasSidebar;
    prevHasNotesRef.current = hasNotes;
  }, [activeLeftPanel, isNotesPanelOpen]);

  // Also clamp on window resize — shrink main first
  useEffect(() => {
    const handleResize = () => {
      const maxTotal = window.innerWidth * MAX_PCT;
      const sidebarW = activeLeftPanel ? SIDEBAR_FIXED_WIDTH : 0;
      const notesW = isNotesPanelOpen ? notesWidthRef.current : 0;
      const handles = 8 + (activeLeftPanel ? 8 : 0) + (isNotesPanelOpen ? 8 : 0);
      // 40px for the fixed-width vertical nav column
      const rawTotal = widthRef.current + 40 + sidebarW + notesW + handles;

      if (rawTotal > maxTotal) {
        let overflow = rawTotal - maxTotal;
        let newMain = widthRef.current;
        let newNotes = notesW;
        const mainShrink = Math.min(overflow, newMain - MIN_MAIN);
        if (mainShrink > 0) { newMain -= mainShrink; overflow -= mainShrink; }
        if (overflow > 0 && isNotesPanelOpen) { const ns = Math.min(overflow, newNotes - 300); if (ns > 0) { newNotes -= ns; } }
        setWidth(newMain);
        if (isNotesPanelOpen) setNotesWidth(newNotes);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [activeLeftPanel, isNotesPanelOpen]);

  const widgets = { 'contacts-links': ContactsLinksWidget, 'candidate-tracking': CandidateTrackingWidget, ...(hasPermission('view_compensation') ? { 'compensation': CompensationWidget } : {}), 'custom-fields': CustomFieldsWidget, 'positions': PositionsWidget, 'education': EducationWidget, 'pipelines': PipelinesWidget, 'documents': ClientDocumentsWidget };
  const widgetProps = { profile, candidateId, onRefresh: handleRefresh, refreshTrigger };
  const isMobile = useIsMobile();
  if (!isOpen) return null;
  const isEditMode = editModeState.isEditMode;

  // Calculate total width, clamped to screen.
  // NAV_COLUMN_WIDTH is the fixed-width vertical nav column that sits
  // between the outer resize handle and any open left panel — accounts
  // for X close / prev / next / presentation / feedback / opportunities.
  // Not rendered on mobile (icons live in the toolbar there instead).
  const NAV_COLUMN_WIDTH = isNavExpanded ? 144 : 40;
  const handles = 8 + (activeLeftPanel ? 8 : 0) + (isNotesPanelOpen ? 8 : 0);
  const rawTotal = width + NAV_COLUMN_WIDTH + (activeLeftPanel ? leftPanelWidth : 0) + (isNotesPanelOpen ? notesWidth : 0) + handles;
  const maxScreen = window.innerWidth * MAX_PCT;
  const totalWidth = Math.min(rawTotal, maxScreen);

  const priorities = [
    { key: 'none', label: 'None', dot: 'bg-gray-300', bg: 'bg-gray-50 border-gray-200 text-gray-600' },
    { key: 'high', label: 'High', dot: 'bg-red-500', bg: 'bg-red-50 border-red-300 text-red-700' },
    { key: 'medium', label: 'Med', dot: 'bg-yellow-400', bg: 'bg-yellow-50 border-yellow-300 text-yellow-700' },
    { key: 'low', label: 'Low', dot: 'bg-green-500', bg: 'bg-green-50 border-green-300 text-green-700' },
  ];
  const presentationTabs = [
    { key: 'presentation-files', label: 'Presentation' },
    { key: 'chat', label: 'Chat' },
    { key: 'activity', label: 'Activity' },
  ];

  // On mobile, only ONE sidebar (any of feedback/presentation/opportunities/notes)
  // can be open at a time — screen real estate is too limited otherwise.
  const closeAllSidebars = () => {
    setIsPresentationOpen(false);
    setIsOpportunitiesOpen(false);
    setIsFeedbackPanelOpen(false);
    setIsNotesPanelOpen(false);
    setShowOutreachPanel(false);
  };
  const openFeedbackPanel = () => {
    if (isFeedbackPanelOpen) { setIsFeedbackPanelOpen(false); }
    else {
      setIsPresentationOpen(false); setIsOpportunitiesOpen(false); setShowOutreachPanel(false); setShowSuggestionsPanel(false); setShowMessagingPanel(false);
      if (isMobile) setIsNotesPanelOpen(false);
      setIsFeedbackPanelOpen(true);
    }
  };
  const openPresentationPanel = () => {
    if (isPresentationOpen) { setIsPresentationOpen(false); }
    else {
      setIsFeedbackPanelOpen(false); setIsOpportunitiesOpen(false); setShowOutreachPanel(false); setShowSuggestionsPanel(false); setShowMessagingPanel(false);
      if (isMobile) setIsNotesPanelOpen(false);
      setIsPresentationOpen(true);
    }
  };
  const openOpportunitiesPanel = () => {
    if (isOpportunitiesOpen) { setIsOpportunitiesOpen(false); }
    else {
      setIsFeedbackPanelOpen(false); setIsPresentationOpen(false); setShowOutreachPanel(false); setShowSuggestionsPanel(false); setShowMessagingPanel(false);
      if (isMobile) setIsNotesPanelOpen(false);
      setIsOpportunitiesOpen(true);
    }
  };
  // Notes toggle — on mobile, opening notes closes other sidebars
  const openNotesPanel = () => {
    if (isNotesPanelOpen) { setIsNotesPanelOpen(false); }
    else {
      if (isMobile) { setIsFeedbackPanelOpen(false); setIsPresentationOpen(false); setIsOpportunitiesOpen(false); }
      setIsNotesPanelOpen(true);
    }
  };

  // ── Presentation Details sidebar ──
  const renderPresentationPanel = () => {
    const currentPriority = cardData?.priority || 'none';
    return (
      <div className={`bg-white flex flex-col overflow-hidden ${isMobile ? "w-full h-full" : "flex-shrink-0 border-r"}`} style={isMobile ? undefined : { width: `${presentationWidth}px` }}>
        <div className="flex-shrink-0 bg-gray-100 border-b border-gray-200 px-3 h-9 flex items-center justify-between overflow-hidden">
          <div className="flex items-center gap-1.5">
            <svg className="w-4 h-4 text-yellow-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>
            <span className="text-xs font-semibold text-gray-800">Introductions</span>
          </div>
          <button onClick={() => setIsPresentationOpen(false)} title="Close"
            className="p-1 text-gray-400 hover:text-gray-700 rounded hover:bg-gray-100">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        {!cardData ? (
          <div className="flex items-center justify-center py-12"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-600"></div></div>
        ) : (
          <>
            {/* Card info + actions */}
            <div className="border-b bg-gray-50 px-4 py-3 flex-shrink-0 space-y-2.5">
              {/* Search actions: link to another search (folded in from the
                  old "Linked Searches" header) + upgrade pools. The list of
                  which searches the candidate is in now lives in the combined
                  selector dropdown below, so the old list is gone. */}
              {/* Adding a candidate to a search happens in the Pipelines widget
                  on the profile, so there's no "+Link" here. This area only
                  surfaces pools that can be UPGRADED to the internal pipeline.
                  Which search you're viewing is chosen in the dropdown below. */}
              {/* Combined search selector — one control for "which search",
                  replacing the old linked-dropdown + status-dropdown pair. The
                  timeline below carries the stage/status. */}
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-1.5 min-w-0 flex-1">
                  {/* Always a dropdown, even with a single search — consistent
                      and obviously interactive. With one search the menu just
                      lists that one. */}
                  <div className="relative min-w-0">
                    <button onClick={() => setShowSearchDropdown(!showSearchDropdown)}
                      className="text-xs px-2.5 py-1 bg-white text-gray-700 border border-gray-300 rounded hover:border-emerald-400 hover:bg-emerald-50 transition-colors flex items-center gap-1.5 max-w-[200px]">
                      <span className="truncate">
                        {(() => {
                          const activeSearch = candidateSearches.find(s => s.card_id === activeCardId);
                          const firm = activeSearch?.firm_name || cardData.firm_name;
                          const name = activeSearch?.pipeline_name || activePipelineName || cardData.pipeline_name;
                          return (
                            <>
                              {firm && <span className="font-semibold text-gray-800">{firm}</span>}
                              {firm && <span className="text-gray-300"> / </span>}
                              <span className={firm ? 'text-gray-600' : ''}>{name}</span>
                            </>
                          );
                        })()}
                      </span>
                      <svg className={`w-3 h-3 text-gray-400 flex-shrink-0 transition-transform ${showSearchDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {showSearchDropdown && (
                      <>
                        <div className="fixed inset-0 z-10" onClick={() => setShowSearchDropdown(false)} />
                        <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 min-w-[200px] py-1">
                          {candidateSearches.length === 0 ? (
                            <p className="px-3 py-1.5 text-xs text-gray-400">No searches</p>
                          ) : candidateSearches.map(sr => (
                            <button key={sr.card_id} onClick={() => { handleSwitchSearch(sr); setShowSearchDropdown(false); }}
                              className={`w-full text-left px-3 py-1.5 text-xs hover:bg-emerald-50 flex items-center justify-between ${sr.card_id === activeCardId ? 'bg-emerald-50 text-emerald-700 font-medium' : 'text-gray-700'}`}>
                              <span className="truncate">
                                {sr.firm_name && <span className="font-semibold text-gray-800">{sr.firm_name}</span>}
                                {sr.firm_name && <span className="text-gray-300"> / </span>}
                                <span className={sr.firm_name ? 'text-gray-500' : ''}>{sr.pipeline_name}</span>
                              </span>
                              {sr.card_id === activeCardId && <svg className="w-3 h-3 text-emerald-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>}
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </div>
                {/* Candidate code — same line, to the right of the selector. */}
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <span className="text-[10px] text-gray-500">Code:</span>
                  {editingCode ? (
                    <span className="flex items-center gap-1">
                      <input type="text" value={codeValue} onChange={(e) => setCodeValue(e.target.value.toUpperCase())}
                        onKeyDown={(e) => { if (e.key === 'Enter') saveCode(); if (e.key === 'Escape') setEditingCode(false); }}
                        className="w-20 px-1.5 py-0.5 text-[11px] border rounded focus:ring-1 focus:ring-emerald-500 font-mono"
                        autoFocus placeholder="VP-01" />
                      <button onClick={saveCode} className="text-green-600 hover:text-green-800"><svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg></button>
                      <button onClick={() => setEditingCode(false)} className="text-gray-400 hover:text-gray-600"><svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg></button>
                    </span>
                  ) : (
                    <button onClick={() => { setCodeValue(cardData.candidate_code || ''); setEditingCode(true); }}
                      className="text-xs font-mono text-gray-600 bg-gray-50 px-1.5 py-0.5 rounded border border-gray-200 hover:bg-gray-100 hover:border-gray-300">
                      {cardData.candidate_code || '—'}
                    </button>
                  )}
                </div>
              </div>

              <div className="space-y-1.5">
                {upgradablePools.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-gray-100">
                    <p className="text-[10px] text-gray-400 mb-1">In these search pools — upgrade to pipeline</p>
                    <div className="space-y-1">
                      {upgradablePools.map(pool => (
                        <div key={pool.pipeline_id}
                          className="flex items-center justify-between px-2 py-1 rounded border border-gray-200 bg-white">
                          <div className="min-w-0 flex-1">
                            <p className="text-[11px] truncate">
                              {pool.firm_name && <span className="font-medium text-gray-800">{pool.firm_name}</span>}
                              {pool.firm_name && <span className="text-gray-300"> / </span>}
                              <span className="text-gray-500">{pool.pipeline_name}</span>
                            </p>
                          </div>
                          <button
                            onClick={() => handleUpgradePool(pool.pipeline_id)}
                            disabled={upgradingPoolId === pool.pipeline_id}
                            className="ml-2 flex items-center gap-1 text-[10px] font-medium text-green-700 hover:text-green-800 disabled:opacity-50 flex-shrink-0"
                            title="Promote to internal pipeline"
                          >
                            {upgradingPoolId === pool.pipeline_id ? 'Upgrading…' : 'Upgrade'}
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Stage timeline — stages ARE the status. Clicking a chevron
                  moves the card. The ⋯ menu handles Disqualified/Withdrew
                  (which recolor the current cell) and Remove. The reason shows
                  under the timeline. */}
              <StageTimeline
                stages={pipelineStages}
                currentStageId={cardData.stage_id}
                currentStageName={cardData.stage_name}
                status={status}
                reason={cardData.rejection_reason}
                busy={deleting}
                onMoveStage={(stageId, stageName) => changeStage(stageId, stageName)}
                onSetBranch={handleTimelineBranch}
                onRemove={handleDeleteCard}
              />

              {/* Assigned (profile avatars, per-user removable) + Priority + Code */}
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <AssignPills
                    cardId={activeCardId}
                    assignedUsers={cardData.assigned_users || []}
                    onChanged={loadCardData}
                  />
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {/* Priority: a None button plus a dropdown for High/Med/Low.
                      None is the default selected state. The dropdown shows the
                      chosen level when one is active, otherwise "Low" — and when
                      opened, Low is the highlighted default. */}
                  {(() => {
                    const none = priorities.find(p => p.key === 'none');
                    const levels = priorities.filter(p => p.key !== 'none'); // high, medium, low
                    const active = priorities.find(p => p.key === currentPriority);
                    const isNone = currentPriority === 'none';
                    // What the dropdown button displays: the active level, or Low as the default hint.
                    const shown = !isNone ? active : priorities.find(p => p.key === 'low');
                    return (
                      <>
                        <button
                          onClick={() => { setShowPriorityMenu(false); handlePriorityChange('none'); }}
                          disabled={updatingPriority}
                          className={`px-2 py-1 text-[10px] font-medium rounded border transition-all ${isNone ? none.bg : 'bg-white text-gray-500 border-gray-200 hover:border-gray-300'} ${updatingPriority ? 'opacity-50' : ''}`}
                        >
                          <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1 ${none.dot}`}></span>None
                        </button>
                        <div className="relative">
                          <button
                            onClick={() => setShowPriorityMenu(o => !o)}
                            disabled={updatingPriority}
                            className={`px-2 py-1 text-[10px] font-medium rounded border transition-all flex items-center gap-1 ${!isNone ? shown.bg : 'bg-white text-gray-500 border-gray-200 hover:border-gray-300'} ${updatingPriority ? 'opacity-50' : ''}`}
                          >
                            <span className={`inline-block w-1.5 h-1.5 rounded-full ${shown.dot}`}></span>
                            {shown.label}
                            <svg className={`w-2.5 h-2.5 flex-shrink-0 transition-transform ${showPriorityMenu ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </button>
                          {showPriorityMenu && (
                            <>
                              <div className="fixed inset-0 z-10" onClick={() => setShowPriorityMenu(false)} />
                              <div className="absolute top-full right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 py-1 min-w-[90px]">
                                {levels.map(p => {
                                  // Low is the highlighted default when nothing specific is chosen.
                                  const highlighted = (!isNone && currentPriority === p.key) || (isNone && p.key === 'low');
                                  return (
                                    <button key={p.key}
                                      onClick={() => { setShowPriorityMenu(false); handlePriorityChange(p.key); }}
                                      className={`w-full text-left px-3 py-1.5 text-[10px] font-medium flex items-center gap-1.5 hover:bg-gray-50 ${highlighted ? 'bg-gray-50' : ''}`}>
                                      <span className={`inline-block w-1.5 h-1.5 rounded-full ${p.dot}`}></span>
                                      {p.label}
                                    </button>
                                  );
                                })}
                              </div>
                            </>
                          )}
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>

              {/* Candidate code now lives on the selector row above. */}


              {/* Reject reason (Disqualified branch) */}
              {showRejectReason && status !== 'rejected' && (
                <div className="p-2.5 bg-red-50 border border-red-200 rounded-lg space-y-2">
                  <textarea
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    placeholder="Reason for disqualification (optional)..."
                    className="w-full text-xs px-2.5 py-1.5 border border-red-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-red-400 resize-none bg-white"
                    rows={2}
                    autoFocus
                  />
                  <div className="flex items-center justify-end gap-1.5">
                    <button onClick={() => { setShowRejectReason(false); setRejectReason(''); }}
                      className="text-[10px] px-2.5 py-1 text-gray-500 border border-gray-200 rounded hover:bg-gray-50">Cancel</button>
                    <button onClick={() => handleUpdateStatus('rejected', rejectReason.trim() || null)}
                      className="text-[10px] px-2.5 py-1 bg-red-600 text-white rounded hover:bg-red-700 font-medium">Confirm</button>
                  </div>
                </div>
              )}

              {/* Withdraw reason */}
              {showWithdrawReason && status !== 'withdrew' && (
                <div className="p-2.5 bg-purple-50 border border-purple-200 rounded-lg space-y-2">
                  <textarea
                    value={withdrawReason}
                    onChange={(e) => setWithdrawReason(e.target.value)}
                    placeholder="Reason for withdrawal (optional)..."
                    className="w-full text-xs px-2.5 py-1.5 border border-purple-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-purple-400 resize-none bg-white"
                    rows={2}
                    autoFocus
                  />
                  <div className="flex items-center justify-end gap-1.5">
                    <button onClick={() => { setShowWithdrawReason(false); setWithdrawReason(''); }}
                      className="text-[10px] px-2.5 py-1 text-gray-500 border border-gray-200 rounded hover:bg-gray-50">Cancel</button>
                    <button onClick={() => handleUpdateStatus('withdrew', withdrawReason.trim() || null)}
                      className="text-[10px] px-2.5 py-1 bg-purple-600 text-white rounded hover:bg-purple-700 font-medium">Confirm Withdrawal</button>
                  </div>
                </div>
              )}
            </div>

            {/* Tabs */}
            <div className="flex border-b px-2 flex-shrink-0">
              {presentationTabs.map(tab => (
                <button key={tab.key} onClick={() => setPresentationTab(tab.key)}
                  className={`px-3 py-2 text-[11px] font-medium border-b-2 transition-colors ${presentationTab === tab.key ? 'border-emerald-500 text-emerald-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Tab content */}
            <div className="flex-1 overflow-y-auto p-4">
              {presentationTab === 'presentation-files' && (
                <div className="space-y-6">
                  <CardDocuments cardId={activeCardId} compact />
                  <div className="border-t border-gray-200 pt-4">
                    {renderPresentationFilesList(presFileInputRef2)}
                  </div>
                </div>
              )}
              {presentationTab === 'chat' && <CardChat cardId={activeCardId} />}
              {presentationTab === 'activity' && <CardActivity cardId={activeCardId} />}
            </div>
          </>
        )}

        {/* REMOVABLE after verifying AssignPills: the inline AssignPills
            component replaced this modal. showAssignModal is no longer set to
            true anywhere, so this never renders. Safe to delete along with the
            showAssignModal state and the AssignUsersModal import once the new
            inline assignment is confirmed working. */}
        {showAssignModal && cardData && (
          <AssignUsersModal cardId={activeCardId} currentAssignments={cardData.assigned_users || []}
            onClose={() => setShowAssignModal(false)} onUpdate={loadCardData} />
        )}
      </div>
    );
  };

  // ── Feedback sidebar ──
  const renderFeedbackPanel = () => (
    <div className={`bg-white flex flex-col overflow-hidden ${isMobile ? "w-full h-full" : "flex-shrink-0 border-r"}`} style={isMobile ? undefined : { width: `${feedbackWidth}px` }}>
      <div className="flex-shrink-0 bg-gray-100 border-b border-gray-200 px-3 h-9 flex items-center justify-between overflow-hidden">
        <div className="flex items-center gap-1.5 overflow-hidden">
          <svg className="w-4 h-4 text-blue-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h9m4-4l2 2 4-4" /></svg>
          <span className="text-xs font-semibold text-gray-800 flex-shrink-0">Interviews</span>
        </div>
        <button onClick={() => setIsFeedbackPanelOpen(false)} title="Close"
          className="p-1 text-gray-400 hover:text-gray-700 rounded hover:bg-gray-100 flex-shrink-0">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
        </button>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto">
      <div className="border-b bg-gray-50 px-4 py-3 flex-shrink-0 space-y-3">
        {candidateSearches.length > 0 && (
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-1.5 min-w-0 flex-1">
              <div className="relative min-w-0">
                <button onClick={() => setShowFeedbackSearchDropdown(!showFeedbackSearchDropdown)}
                  className="text-xs px-2.5 py-1 bg-white text-gray-700 border border-gray-300 rounded hover:border-emerald-400 hover:bg-emerald-50 transition-colors flex items-center gap-1.5 max-w-[200px]">
                  <span className="truncate">
                    {(() => {
                      const activeSearch = candidateSearches.find(s => s.card_id === activeCardId);
                      const firm = activeSearch?.firm_name || cardData?.firm_name;
                      const name = activeSearch?.pipeline_name || activePipelineName || cardData?.pipeline_name;
                      return (
                        <>
                          {firm && <span className="font-semibold text-gray-800">{firm}</span>}
                          {firm && <span className="text-gray-300"> / </span>}
                          <span className={firm ? 'text-gray-600' : ''}>{name}</span>
                        </>
                      );
                    })()}
                  </span>
                  <svg className={`w-3 h-3 text-gray-400 flex-shrink-0 transition-transform ${showFeedbackSearchDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {showFeedbackSearchDropdown && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setShowFeedbackSearchDropdown(false)} />
                    <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 min-w-[200px] py-1">
                      {candidateSearches.map(sr => (
                        <button key={sr.card_id} onClick={() => { handleSwitchSearch(sr); setShowFeedbackSearchDropdown(false); }}
                          className={`w-full text-left px-3 py-1.5 text-xs hover:bg-emerald-50 flex items-center justify-between ${sr.card_id === activeCardId ? 'bg-emerald-50 text-emerald-700 font-medium' : 'text-gray-700'}`}>
                          <span className="truncate">
                            {sr.firm_name && <span className="font-semibold text-gray-800">{sr.firm_name}</span>}
                            {sr.firm_name && <span className="text-gray-300"> / </span>}
                            <span className={sr.firm_name ? 'text-gray-500' : ''}>{sr.pipeline_name}</span>
                          </span>
                          {sr.card_id === activeCardId && <svg className="w-3 h-3 text-emerald-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
            {/* Candidate code — same line, right-aligned (matches Introductions). */}
            {cardData && (
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <span className="text-[10px] text-gray-500">Code:</span>
                {!isClientUser && editingFeedbackCode ? (
                  <span className="flex items-center gap-1">
                    <input type="text" value={feedbackCodeValue} onChange={(e) => setFeedbackCodeValue(e.target.value.toUpperCase())}
                      onKeyDown={(e) => { if (e.key === 'Enter') saveFeedbackCode(); if (e.key === 'Escape') setEditingFeedbackCode(false); }}
                      className="w-20 px-1.5 py-0.5 text-[11px] border rounded focus:ring-1 focus:ring-emerald-500 font-mono"
                      autoFocus placeholder="VP-01" />
                    <button onClick={saveFeedbackCode} className="text-green-600 hover:text-green-800"><svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg></button>
                    <button onClick={() => setEditingFeedbackCode(false)} className="text-gray-400 hover:text-gray-600"><svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg></button>
                  </span>
                ) : !isClientUser ? (
                  <button onClick={() => { setFeedbackCodeValue(cardData.candidate_code || ''); setEditingFeedbackCode(true); }}
                    className="text-xs font-mono text-gray-600 bg-gray-50 px-1.5 py-0.5 rounded border border-gray-200 hover:bg-gray-100 hover:border-gray-300">
                    {cardData.candidate_code || '—'}
                  </button>
                ) : (
                  <span className="text-xs font-mono text-gray-600 bg-gray-50 px-1.5 py-0.5 rounded border border-gray-200">{cardData.candidate_code || '—'}</span>
                )}
              </div>
            )}
          </div>
        )}

        {/* Interview availability — recruiter generates a public link the
            candidate uses to pick times. */}

        {/* Attached Files */}
        {(presentationFiles.length > 0 || !isClientUser) && (
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Attached Files</span>
              {!isClientUser && (
                <button onClick={() => presFileInputRef.current?.click()} disabled={uploadingPresFile}
                  className="text-[10px] text-emerald-700 hover:text-emerald-800 font-medium disabled:opacity-50">
                  {uploadingPresFile ? 'Uploading...' : '+ Attach File'}
                </button>
              )}
            </div>
            <input ref={presFileInputRef} type="file" className="hidden" onChange={handleUploadPresFile}
              accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.jpg,.jpeg,.png,.gif" />
            {presentationFiles.length > 0 && (
              <div className="space-y-1">
                {presentationFiles.map(f => (
                  <div key={f.id} className="flex items-center gap-2 group">
                    <button onClick={() => openPresFile(f.id, f.filename)}
                      className="flex-1 min-w-0 flex items-center gap-1.5 px-2 py-1.5 bg-white border rounded hover:border-emerald-300 hover:bg-emerald-50 transition-colors text-left">
                      <svg className="w-3.5 h-3.5 flex-shrink-0 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                      <span className="text-[11px] text-gray-700 truncate">{f.filename}</span>
                    </button>
                    {!isClientUser && (
                      <button onClick={() => handleDeletePresFile(f.id)}
                        className="opacity-0 group-hover:opacity-100 p-0.5 text-gray-400 hover:text-red-500 transition-opacity flex-shrink-0">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {isPipelineContext && (
          <>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] font-medium text-gray-500">{isClientUser ? 'Rate Resume:' : 'Resume Rating:'}</span>
              {isClientUser ? (
                <Tooltip text={overallAverage > 0 ? `Overall: ${overallAverage}/5` : 'Rate resume'} placement="bottom">
                  <div className="flex items-center gap-0.5">
                    {[1,2,3,4,5].map(s => <button key={s} onClick={() => handleSaveRating(s)} onMouseEnter={() => setHoverRating(s)} onMouseLeave={() => setHoverRating(0)} disabled={savingRating} className={`text-lg leading-none transition-transform hover:scale-125 ${hoverRating > 0 ? (s <= hoverRating ? 'text-yellow-400' : 'text-gray-300') : (s <= displayStars ? 'text-yellow-400' : 'text-gray-300')}`}>★</button>)}
                  </div>
                </Tooltip>
              ) : (
                <div className="flex items-center gap-0.5">
                  {[1,2,3,4,5].map(s => <span key={s} className={`text-lg leading-none ${s <= displayStars ? 'text-yellow-400' : 'text-gray-300'}`}>★</span>)}
                </div>
              )}
              {overallAverage > 0 && <span className="text-[10px] text-gray-400 ml-1">{overallAverage}</span>}
            </div>
            {hasPermission('approve_reject') && (
            <div className="flex items-center gap-1.5">
              {status !== 'withdrew' ? (
                <>
                  <button onClick={() => { handleUpdateStatus('approved'); setShowRejectReason(false); }} className={`px-2.5 py-1.5 rounded-lg flex items-center gap-1.5 transition-colors text-xs font-medium ${status === 'approved' ? 'bg-green-100 text-green-700 ring-1 ring-green-300' : 'text-gray-400 hover:bg-green-50 hover:text-green-600 border border-gray-200'}`}>
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                    {status === 'approved' ? 'Approved' : 'Approve'}
                  </button>
                  <button onClick={() => { if (status === 'rejected') return; setShowRejectReason(!showRejectReason); }} className={`px-2.5 py-1.5 rounded-lg flex items-center gap-1.5 transition-colors text-xs font-medium ${status === 'rejected' ? 'bg-red-100 text-red-700 ring-1 ring-red-300' : showRejectReason ? 'bg-red-50 text-red-600 border border-red-300' : 'text-gray-400 hover:bg-red-50 hover:text-red-600 border border-gray-200'}`}>
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    {status === 'rejected' ? 'Rejected' : 'Reject'}
                  </button>
                </>
              ) : (
                <span className="px-2.5 py-1.5 rounded-lg text-xs font-medium bg-purple-100 text-purple-700 ring-1 ring-purple-300 flex items-center gap-1.5">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>
                  Withdrew
                </span>
              )}
              <div className="relative">
                <button onClick={() => setShowReviewActions(!showReviewActions)} className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><circle cx="10" cy="4" r="1.5"/><circle cx="10" cy="10" r="1.5"/><circle cx="10" cy="16" r="1.5"/></svg>
                </button>
                {showReviewActions && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setShowReviewActions(false)} />
                    <div className="absolute top-full right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 min-w-[160px] py-1">
                      {status === 'withdrew' ? (
                        <>
                          <button onClick={() => { setShowReviewActions(false); handleUpdateStatus('approved'); }}
                            className="w-full text-left px-3 py-1.5 text-xs text-green-700 hover:bg-green-50 flex items-center gap-2">
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                            Override → Approve
                          </button>
                          <button onClick={() => { setShowReviewActions(false); setShowRejectReason(true); setShowWithdrawReason(false); }}
                            className="w-full text-left px-3 py-1.5 text-xs text-red-700 hover:bg-red-50 flex items-center gap-2">
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                            Override → Reject
                          </button>
                        </>
                      ) : (
                        <button onClick={() => { setShowReviewActions(false); setShowWithdrawReason(true); setShowRejectReason(false); }}
                          className="w-full text-left px-3 py-1.5 text-xs text-purple-700 hover:bg-purple-50 flex items-center gap-2">
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>
                          Mark as Withdrew
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
            )}
          </div>
          {showRejectReason && status !== 'rejected' && (
            <div className="mt-2 p-2.5 bg-red-50 border border-red-200 rounded-lg space-y-2">
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Reason for rejection (optional)..."
                className="w-full text-xs px-2.5 py-1.5 border border-red-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-red-400 resize-none bg-white"
                rows={2}
                autoFocus
              />
              <div className="flex items-center justify-end gap-1.5">
                <button onClick={() => { setShowRejectReason(false); setRejectReason(''); }}
                  className="text-[10px] px-2.5 py-1 text-gray-500 border border-gray-200 rounded hover:bg-gray-50">Cancel</button>
                <button onClick={() => handleUpdateStatus('rejected', rejectReason.trim() || null)}
                  className="text-[10px] px-2.5 py-1 bg-red-600 text-white rounded hover:bg-red-700 font-medium">Confirm Reject</button>
              </div>
            </div>
          )}
          {showWithdrawReason && status !== 'withdrew' && (
            <div className="mt-2 p-2.5 bg-purple-50 border border-purple-200 rounded-lg space-y-2">
              <textarea
                value={withdrawReason}
                onChange={(e) => setWithdrawReason(e.target.value)}
                placeholder="Reason for withdrawal (optional)..."
                className="w-full text-xs px-2.5 py-1.5 border border-purple-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-purple-400 resize-none bg-white"
                rows={2}
                autoFocus
              />
              <div className="flex items-center justify-end gap-1.5">
                <button onClick={() => { setShowWithdrawReason(false); setWithdrawReason(''); }}
                  className="text-[10px] px-2.5 py-1 text-gray-500 border border-gray-200 rounded hover:bg-gray-50">Cancel</button>
                <button onClick={() => handleUpdateStatus('withdrew', withdrawReason.trim() || null)}
                  className="text-[10px] px-2.5 py-1 bg-purple-600 text-white rounded hover:bg-purple-700 font-medium">Confirm Withdrawal</button>
              </div>
            </div>
          )}
          </>
        )}
        {(rating > 0 || interviewFeedback.some(f => f.rating > 0)) && (
          <div className="flex items-center gap-4 pt-2 border-t border-gray-200">
            {rating > 0 && <div className="flex items-center gap-1"><span className="text-[10px] text-gray-500">Resume:</span><span className="text-xs">{[1,2,3,4,5].map(s => <span key={s} className={s <= rating ? 'text-yellow-400' : 'text-gray-300'}>★</span>)}</span></div>}
            {(() => { const rr = interviewFeedback.filter(f => f.rating > 0).map(f => f.rating); if (!rr.length) return null; const avg = (rr.reduce((a,b)=>a+b,0)/rr.length).toFixed(1); return <div className="flex items-center gap-1"><span className="text-[10px] text-gray-500">Interviews:</span><span className="text-xs">{[1,2,3,4,5].map(s => <span key={s} className={s <= Math.round(parseFloat(avg)) ? 'text-yellow-400' : 'text-gray-300'}>★</span>)}</span><span className="text-[10px] text-gray-400">{avg}</span></div>; })()}
          </div>
        )}
      </div>

      {/* Availability + Interviews — below Files & Rating, above Feedback */}
      {!isClientUser && !feedbackExpanded && (
        <div className="border-b bg-gray-50 px-4 py-3 flex-shrink-0 space-y-3">
          <AvailabilitySection
            candidateId={candidateId}
            candidateName={profile?.name}
            candidateEmail={profile?.email}
            candidatePhone={profile?.phone}
            candidatePhones={candidatePhones}
            cardId={activeCardId}
            pipelineId={activePipelineId}
            pipelineName={pipelineName}
            onSchedule={(slot) => setSchedulePrefill(slot)}
          />
          {activeCardId && (
            <InterviewsSection
              candidateId={candidateId}
              candidateName={profile?.name}
              candidateEmail={profile?.email}
              cardId={activeCardId}
              pipelineId={activePipelineId}
              pipelineName={pipelineName}
              prefill={schedulePrefill}
              onPrefillConsumed={() => setSchedulePrefill(null)}
            />
          )}
        </div>
      )}

      {!readOnly && isPipelineContext && (
        <div className="border-b bg-emerald-50 px-4 py-3 space-y-2 flex-shrink-0">
          <p className="text-[10px] font-semibold text-emerald-700 uppercase tracking-wide">Add Feedback</p>
          <div className="grid grid-cols-2 gap-2">
            <select value={newFeedback.stage_id} onChange={(e) => setNewFeedback({...newFeedback, stage_id: e.target.value})} className="px-2 py-1.5 text-xs border rounded focus:ring-1 focus:ring-emerald-500 bg-white"><option value="">Select stage...</option>{allStages.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}</select>
            <input type="text" value={newFeedback.interviewer_name} onChange={(e) => setNewFeedback({...newFeedback, interviewer_name: e.target.value})} className="px-2 py-1.5 text-xs border rounded focus:ring-1 focus:ring-emerald-500 bg-white" placeholder="Interviewer" />
          </div>
          {/* Meeting tag selector */}
          <select value={newFeedback.meeting_id} onChange={(e) => setNewFeedback({...newFeedback, meeting_id: e.target.value})}
            className="w-full px-2 py-1.5 text-xs border rounded focus:ring-1 focus:ring-emerald-500 bg-white">
            <option value="">Link to meeting (optional)...</option>
            {cardMeetings.map(m => (
              <option key={m.id} value={m.id}>
                📅 {m.title} — {new Date(m.start_time).toLocaleDateString('en-US',{month:'short',day:'numeric'})} {new Date(m.start_time).toLocaleTimeString('en-US',{hour:'numeric',minute:'2-digit',hour12:true})}
              </option>
            ))}
          </select>
          <div className="flex items-center gap-2"><span className="text-[10px] text-gray-500">Rating:</span><div className="flex gap-0.5">{[1,2,3,4,5].map(s => <button key={s} onClick={() => setNewFeedback({...newFeedback, rating: s})} className={`text-base cursor-pointer hover:scale-110 transition ${s <= newFeedback.rating ? 'text-yellow-400' : 'text-gray-300'}`}>★</button>)}</div></div>
          <textarea value={newFeedback.feedback_text} onChange={(e) => setNewFeedback({...newFeedback, feedback_text: e.target.value})} rows="3" className="w-full px-2 py-1.5 text-xs border rounded focus:ring-1 focus:ring-emerald-500 bg-white" placeholder="Share interview feedback..." />
          <button onClick={handleSubmitFeedback} disabled={savingFeedback || !newFeedback.stage_id || !newFeedback.feedback_text.trim()} className="px-3 py-1.5 text-xs bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50 w-full">{savingFeedback ? 'Saving...' : 'Add Feedback'}</button>
        </div>
      )}
      <div className={`${feedbackExpanded ? 'flex-1 min-h-0 overflow-y-auto' : 'flex-shrink-0'} px-4 py-3`}>
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Interview Feedback</span>
          <button onClick={() => setFeedbackExpanded(v => !v)}
            title={feedbackExpanded ? 'Collapse' : 'Expand to focus on feedback'}
            className="p-0.5 text-gray-400 hover:text-emerald-600 rounded hover:bg-gray-100">
            {feedbackExpanded
              ? <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5l5.25 5.25" /></svg>
              : <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" /></svg>}
          </button>
        </div>
        {feedbackLoading ? <div className="flex items-center justify-center py-12"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-600"></div></div>
        : interviewFeedback.length === 0 ? <p className="text-xs text-gray-400 text-center py-12">No interview feedback yet</p>
        : <div className="space-y-3">{interviewFeedback.map((fb, idx) => (
            <div key={idx} className="border rounded-lg p-3">
              <div className="flex items-center justify-between mb-1"><div><span className="text-xs font-semibold text-gray-900">{fb.stage_name}</span>{fb.interviewer_name && <span className="text-[10px] text-gray-500 ml-2">by {fb.interviewer_name}</span>}</div>{fb.rating > 0 && <span className="text-xs text-yellow-500">{'★'.repeat(Number(fb.rating))}{'☆'.repeat(5-Number(fb.rating))}</span>}</div>
              {fb.meeting_start_time && (
                <div className="flex items-center gap-1.5 mb-1.5 px-2 py-1 bg-emerald-50 border border-emerald-200 rounded text-[10px]">
                  <svg className="w-3 h-3 text-emerald-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                  <span className="text-emerald-700 font-medium">{new Date(fb.meeting_start_time).toLocaleDateString('en-US',{weekday:'short',month:'short',day:'numeric'})}</span>
                  <span className="text-emerald-700">{new Date(fb.meeting_start_time).toLocaleTimeString('en-US',{hour:'numeric',minute:'2-digit',hour12:true})} – {new Date(fb.meeting_end_time).toLocaleTimeString('en-US',{hour:'numeric',minute:'2-digit',hour12:true})}</span>
                  {fb.meeting_url && <a href={fb.meeting_url} target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:underline ml-auto">📹 Join</a>}
                </div>
              )}
              <p className="text-xs text-gray-700 leading-relaxed">{fb.feedback_text}</p>
              <p className="text-[10px] text-gray-400 mt-1.5">{new Date(fb.created_at).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'})}</p>
            </div>))}</div>}
      </div>
      </div>
    </div>
  );

  // ── Opportunities sidebar ──
  const renderOpportunitiesPanel = () => {
    const statusStyles = { open: 'bg-green-100 text-green-700', on_hold: 'bg-yellow-100 text-yellow-700', filled: 'bg-blue-100 text-blue-700', closed: 'bg-gray-100 text-gray-500' };
    const candStatuses = ['potential', 'contacted', 'interested', 'not_interested', 'placed'];
    const candStatusLabels = { potential: 'Potential', contacted: 'Contacted', interested: 'Interested', not_interested: 'Not Interested', placed: 'Placed' };
    return (
      <div className={`bg-white flex flex-col overflow-hidden ${isMobile ? "w-full h-full" : "flex-shrink-0 border-r"}`} style={isMobile ? undefined : { width: `${presentationWidth}px` }}>
        <div className="flex-shrink-0 bg-gray-100 border-b border-gray-200 px-3 h-9 flex items-center justify-between overflow-hidden">
          <div className="flex items-center gap-1.5">
            <svg className="w-4 h-4 text-teal-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
            <span className="text-xs font-semibold text-gray-800">Opportunities</span>
          </div>
          <button onClick={() => setIsOpportunitiesOpen(false)} title="Close"
            className="p-1 text-gray-400 hover:text-gray-700 rounded hover:bg-gray-100">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        {oppRolesLoading ? (
          <div className="flex items-center justify-center py-12"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-600"></div></div>
        ) : (
          <div className="flex-1 overflow-y-auto">
            {/* ── Opportunity Roles section ── */}
            <div className="border-b">
              <div className="px-3 py-2 bg-gray-50 flex items-center justify-between">
                <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Opportunity Roles</span>
                <span className="text-[10px] text-gray-400">{oppRoles.filter(r => r.status === 'open').length} open</span>
              </div>
              {oppRoles.length === 0 ? (
                <div className="px-3 py-4 text-center">
                  <p className="text-[11px] text-gray-400">No roles tracked yet</p>
                  <p className="text-[10px] text-gray-400">Add roles on the Partnerships page</p>
                </div>
              ) : (
                oppRoles.map(role => {
                  const isLinked = candidateRoleIds.has(role.id);
                  const isToggling = togglingRole === role.id;
                  return (
                    <div key={role.id} className={`border-b last:border-0 ${isLinked ? 'bg-green-50/50' : ''}`}>
                      <div className="px-3 py-2.5 flex items-start gap-2">
                        <button onClick={() => toggleCandidateRole(role.id)} disabled={isToggling}
                          className={`mt-0.5 flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${isLinked ? 'bg-green-500 border-green-500 text-white' : 'border-gray-300 hover:border-green-400'} ${isToggling ? 'opacity-50' : ''}`}>
                          {isLinked && <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
                        </button>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className="text-xs font-semibold text-gray-900 truncate">{role.title}</span>
                            <span className={`px-1 py-0 text-[9px] font-medium rounded ${statusStyles[role.status] || statusStyles.open}`}>{role.status?.replace('_', ' ')}</span>
                          </div>
                          {(role.company || role.location) && <p className="text-[11px] text-gray-500 truncate">{role.company}{role.company && role.location ? ' · ' : ''}{role.location}</p>}
                          {role.description && <p className="text-[10px] text-gray-400 mt-0.5 line-clamp-2">{role.description}</p>}
                          {role.contact_name && <p className="text-[10px] text-gray-400 mt-0.5">Contact: {role.contact_name}</p>}
                          {role.url && <a href={role.url} target="_blank" rel="noopener noreferrer" className="text-[10px] text-emerald-600 hover:underline mt-0.5 inline-block">View posting →</a>}
                          {isLinked && (
                            <div className="mt-1.5 flex items-center gap-1">
                              <span className="text-[9px] text-gray-400">Status:</span>
                              <select defaultValue="potential" onChange={(e) => updateCandidateRoleStatus(role.id, e.target.value)}
                                className="text-[10px] border rounded px-1 py-0.5 text-gray-600 focus:ring-1 focus:ring-emerald-500 bg-white" onClick={(e) => e.stopPropagation()}>
                                {candStatuses.map(s => <option key={s} value={s}>{candStatusLabels[s]}</option>)}
                              </select>
                            </div>
                          )}
                        </div>
                        <span className="text-[10px] text-gray-400 flex-shrink-0 mt-0.5">{role.candidate_count || 0}</span>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* ── Executive Searches section ── */}
            <div>
              <div className="px-3 py-2 bg-gray-50 flex items-center justify-between">
                <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Executive Searches</span>
                <span className="text-[10px] text-gray-400">{oppSearches.length} active</span>
              </div>
              {oppSearches.length === 0 ? (
                <div className="px-3 py-4 text-center">
                  <p className="text-[11px] text-gray-400">No active executive searches</p>
                </div>
              ) : (
                oppSearches.map(search => {
                  const isIn = candidateSearchIds.has(search.id);
                  return (
                    <div key={search.id} className={`border-b last:border-0 ${isIn ? 'bg-emerald-50/50' : ''}`}>
                      <div className="px-3 py-2.5 flex items-start gap-2">
                        {/* In-search indicator */}
                        <div className={`mt-0.5 flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center ${isIn ? 'bg-emerald-500 text-white' : 'bg-gray-100'}`}>
                          {isIn ? (
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                          ) : (
                            <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className="text-xs font-semibold text-gray-900 truncate cursor-pointer hover:text-emerald-700"
                              onClick={() => window.open(`/search/${search.id}`, '_blank')}>
                              {search.name}
                            </span>
                            {isIn && <span className="px-1 py-0 text-[9px] font-medium rounded bg-emerald-100 text-emerald-700">In Search</span>}
                          </div>
                          <p className="text-[11px] text-gray-500 truncate">
                            {search.firm_name && <span>{search.firm_name}</span>}
                            {search.location && <span>{search.firm_name ? ' · ' : ''}{search.location}</span>}
                            {search.compensation_range && <span> · {search.compensation_range}</span>}
                          </p>
                          {search.description && <p className="text-[10px] text-gray-400 mt-0.5 line-clamp-2">{search.description}</p>}
                          <div className="flex items-center gap-3 mt-1">
                            <span className="text-[10px] text-gray-400">{search.card_count || 0} candidates</span>
                            {search.created_by_name && <span className="text-[10px] text-gray-400">by {search.created_by_name}</span>}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>
    );
  };


  return (
    <>
      {/* Sidebar entrance/exit animations — kept simple: small fade + slight slide.
          ~180ms feels snappy without looking jumpy. */}
      <style>{`
        @keyframes sidebar-in-left { 0% { opacity: 0; transform: translateX(-24px); } 100% { opacity: 1; transform: translateX(0); } }
        @keyframes sidebar-in-right { 0% { opacity: 0; transform: translateX(24px); } 100% { opacity: 1; transform: translateX(0); } }
        @keyframes sidebar-in-up { 0% { opacity: 0; transform: translateY(24px); } 100% { opacity: 1; transform: translateY(0); } }
        .sidebar-anim-left { animation: sidebar-in-left 180ms ease-out both; }
        .sidebar-anim-right { animation: sidebar-in-right 180ms ease-out both; }
        .sidebar-anim-up { animation: sidebar-in-up 200ms ease-out both; }
      `}</style>
      <div
        ref={panelRef}
        className={`fixed bg-gray-50 shadow-2xl z-50 ${isMobile ? 'inset-0 flex flex-col' : 'top-0 right-0 bottom-0 flex'}`}
        style={isMobile ? undefined : { width: `${totalWidth}px` }}
      >
        {/* ── Desktop: left sidebar (feedback / presentation / opportunities) ── */}
        {!isMobile && activeLeftPanel && (
          <ResizeHandle onMouseDown={startSideResize} isActive={resizingTarget === 'side'} />
        )}
        {!isMobile && !activeLeftPanel && (
          <ResizeHandle onMouseDown={startMainResize} isActive={resizingTarget === 'main'} />
        )}

        {/* ── NEW: vertical nav column ──
            40px wide, sits between the resize handle (left) and any
            open left panel / main content (right). Always visible on
            desktop. Contains:
              • X close (with same unsaved-changes prompt as the old
                toolbar close)
              • Up / Down arrows for prev/next candidate navigation.
                Disabled if parent doesn't provide callbacks or is at
                a boundary.
              • Presentation Details, Candidate Review (feedback),
                Opportunities — same gates + active-state colors as
                the toolbar versions they replace.
            On mobile, this column is skipped; the icons remain in the
            toolbar for that viewport. */}
        {!isMobile && (
          <div
            className={`flex-shrink-0 bg-gray-100 border-r border-gray-200 flex flex-col pt-2 pb-8 gap-1 relative ${isNavExpanded ? 'items-stretch px-2' : 'items-center'}`}
            style={{ width: `${NAV_COLUMN_WIDTH}px` }}
          >
            {/* Close */}
            <Tooltip text={isNavExpanded ? '' : "Close (Esc)"} placement="right">
              <button
                onClick={() => { if (isEditMode && editModeState.hasChanges) { if (window.confirm('Unsaved changes. Discard?')) { editModeState.onCancel?.(); onClose(); } } else { if (isEditMode) editModeState.onCancel?.(); onClose(); } }}
                className={`h-7 rounded flex items-center transition-colors text-gray-400 hover:text-red-500 hover:bg-red-50 ${isNavExpanded ? 'w-full px-1.5 gap-2 justify-start' : 'w-7 justify-center'}`}
              >
                <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                {isNavExpanded && <span className="text-xs font-medium truncate">Close</span>}
              </button>
            </Tooltip>

            {/* Prev candidate */}
            <Tooltip text={isNavExpanded ? '' : "Previous candidate"} placement="right">
              <button
                onClick={() => onPrevCandidate?.()}
                disabled={!onPrevCandidate || !hasPrev}
                className={`h-7 rounded flex items-center transition-colors text-gray-400 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-gray-400 ${isNavExpanded ? 'w-full px-1.5 gap-2 justify-start' : 'w-7 justify-center'}`}
              >
                <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" /></svg>
                {isNavExpanded && <span className="text-xs font-medium truncate">Previous</span>}
              </button>
            </Tooltip>

            {/* Next candidate */}
            <Tooltip text={isNavExpanded ? '' : "Next candidate"} placement="right">
              <button
                onClick={() => onNextCandidate?.()}
                disabled={!onNextCandidate || !hasNext}
                className={`h-7 rounded flex items-center transition-colors text-gray-400 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-gray-400 ${isNavExpanded ? 'w-full px-1.5 gap-2 justify-start' : 'w-7 justify-center'}`}
              >
                <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                {isNavExpanded && <span className="text-xs font-medium truncate">Next</span>}
              </button>
            </Tooltip>

            <div className={`h-px bg-gray-200 my-1 ${isNavExpanded ? 'w-full' : 'w-4'}`} />

            {/* Suggestions — AI reads notes + call transcripts and proposes
                profile field updates. Recruiter/admin only: it writes to
                client_candidate_data, which clients don't own. Amber to
                separate it from the panel-openers above (blue/green/purple)
                and to read as "needs your attention" rather than a section. */}
            {!isClientUser && (
              <Tooltip text={isNavExpanded ? '' : "Suggestions"} placement="right">
                <button
                  onClick={openSuggestionsPanel}
                  className={`h-7 rounded flex items-center transition-colors ${showSuggestionsPanel ? 'text-amber-600 bg-amber-100' : 'text-gray-400 hover:text-amber-600 hover:bg-amber-50'} ${isNavExpanded ? 'w-full px-1.5 gap-2 justify-start' : 'w-7 justify-center'} relative`}
                >
                  <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" /></svg>
                  {isNavExpanded
                    ? <span className="text-xs font-medium truncate">Suggestions{suggestionCount > 0 ? ` (${suggestionCount})` : ''}</span>
                    : (suggestionCount > 0 && (
                        <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 bg-amber-500 text-white rounded-full border-2 border-white text-[10px] font-semibold flex items-center justify-center leading-none">
                          {suggestionCount > 99 ? '99+' : suggestionCount}
                        </span>
                      ))}
                </button>
              </Tooltip>
            )}

            {/* Opportunities — recruiter/admin only. Teal to sit distinct from
                the amber Suggestions above and the purple Outreach below. */}
            {!isClientUser && (
              <Tooltip text={isNavExpanded ? '' : "Opportunities"} placement="right">
                <button
                  onClick={openOpportunitiesPanel}
                  className={`h-7 rounded flex items-center transition-colors ${isOpportunitiesOpen ? 'text-teal-600 bg-teal-100' : 'text-gray-400 hover:text-teal-600 hover:bg-teal-50'} ${isNavExpanded ? 'w-full px-1.5 gap-2 justify-start' : 'w-7 justify-center'}`}
                >
                  <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                  {isNavExpanded && <span className="text-xs font-medium truncate">Opportunities</span>}
                </button>
              </Tooltip>
            )}

            {/* Add to Campaign → labeled "Outreach" */}
            {!isClientUser && (
              <Tooltip text={isNavExpanded ? '' : "Outreach"} placement="right">
                <button
                  onClick={openOutreachPanel}
                  className={`h-7 rounded flex items-center transition-colors ${showOutreachPanel ? 'text-purple-600 bg-purple-100' : 'text-gray-400 hover:text-purple-600 hover:bg-purple-50'} ${isNavExpanded ? 'w-full px-1.5 gap-2 justify-start' : 'w-7 justify-center'}`}
                >
                  <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" /></svg>
                  {isNavExpanded && <span className="text-xs font-medium truncate">Outreach</span>}
                </button>
              </Tooltip>
            )}

            {/* Messaging → this candidate's SMS reply thread */}
            {!isClientUser && (
              <Tooltip text={isNavExpanded ? '' : "Messaging"} placement="right">
                <button
                  onClick={openMessagingPanel}
                  className={`h-7 rounded flex items-center transition-colors ${showMessagingPanel ? 'text-emerald-700 bg-emerald-100' : 'text-gray-400 hover:text-emerald-700 hover:bg-emerald-50'} ${isNavExpanded ? 'w-full px-1.5 gap-2 justify-start' : 'w-7 justify-center'}`}
                >
                  <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.86 9.86 0 01-4-.83l-5 1.5 1.5-4.5A7.94 7.94 0 013 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>
                  {isNavExpanded && <span className="text-xs font-medium truncate">Messaging</span>}
                </button>
              </Tooltip>
            )}

            {/* Presentation Details → labeled "Introductions" */}
            {!isClientUser && isPipelineContext && (
              <Tooltip text={isNavExpanded ? '' : "Introductions"} placement="right">
                <button
                  onClick={openPresentationPanel}
                  className={`h-7 rounded flex items-center transition-colors ${isPresentationOpen ? 'text-yellow-700 bg-yellow-100' : 'text-gray-400 hover:text-yellow-700 hover:bg-yellow-50'} ${isNavExpanded ? 'w-full px-1.5 gap-2 justify-start' : 'w-7 justify-center'}`}
                >
                  <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>
                  {isNavExpanded && <span className="text-xs font-medium truncate">Introductions</span>}
                </button>
              </Tooltip>
            )}

            {/* Candidate Review (feedback) → labeled "Interviews" */}
            {isPipelineContext && (
              <Tooltip text={isNavExpanded ? '' : "Interviews"} placement="right">
                <button
                  onClick={openFeedbackPanel}
                  className={`h-7 rounded flex items-center transition-colors ${isFeedbackPanelOpen ? 'text-blue-600 bg-blue-100' : 'text-gray-400 hover:text-blue-600 hover:bg-blue-50'} ${isNavExpanded ? 'w-full px-1.5 gap-2 justify-start' : 'w-7 justify-center'}`}
                >
                  <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h9m4-4l2 2 4-4" /></svg>
                  {isNavExpanded && <span className="text-xs font-medium truncate">Interviews</span>}
                </button>
              </Tooltip>
            )}

            {/* Spacer — pushes Edit Profile + Export Profile to the
                bottom of the nav column. flex-1 fills all remaining
                vertical space between the panel-opener buttons above
                and the utility buttons below. */}
            <div className="flex-1" />

            {/* Edit Profile — panel-level action moved from the right
                toolbar. Bottom-anchored group. */}
            <Tooltip text={isNavExpanded ? '' : "Edit Layout"} placement="right">
              <button
                onClick={() => editModeState.setIsEditMode?.(true)}
                className={`h-7 rounded flex items-center transition-colors text-gray-400 hover:text-gray-700 hover:bg-gray-100 ${isNavExpanded ? 'w-full px-1.5 gap-2 justify-start' : 'w-7 justify-center'}`}
              >
                <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                {isNavExpanded && <span className="text-xs font-medium truncate">Edit Layout</span>}
              </button>
            </Tooltip>

            {/* Export Profile — placeholder for now (feature not built).
                Neutral gray palette signals a secondary utility action. */}
            <Tooltip text={isNavExpanded ? '' : "Export Profile"} placement="right">
              <button
                onClick={() => alert('Export coming soon')}
                className={`h-7 rounded flex items-center transition-colors text-gray-400 hover:text-gray-700 hover:bg-gray-100 ${isNavExpanded ? 'w-full px-1.5 gap-2 justify-start' : 'w-7 justify-center'}`}
              >
                <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
                {isNavExpanded && <span className="text-xs font-medium truncate">Export Profile</span>}
              </button>
            </Tooltip>

            {/* Expand/collapse toggle — sits at the bottom, straddling
                the right border of the nav column (the border that
                touches the candidate profile widgets). Half-in, half-out
                is the classic "collapse handle" pattern. */}
            <button
              onClick={() => setIsNavExpanded(v => !v)}
              title={isNavExpanded ? 'Collapse' : 'Expand'}
              className="absolute right-0 bottom-3 translate-x-1/2 w-5 h-5 bg-white border border-gray-300 rounded-full flex items-center justify-center hover:bg-gray-50 shadow-sm z-10 text-gray-500 hover:text-gray-800"
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isNavExpanded ? "M15 19l-7-7 7-7" : "M9 5l7 7-7 7"} />
              </svg>
            </button>
          </div>
        )}

        {!isMobile && activeLeftPanel && (
          <div className="sidebar-anim-left flex">
            {activeLeftPanel === 'presentation' ? renderPresentationPanel()
              : activeLeftPanel === 'opportunities' ? renderOpportunitiesPanel()
              : activeLeftPanel === 'suggestions' ? (
                <div className="bg-white flex flex-col overflow-hidden flex-shrink-0 border-r" style={{ width: `${presentationWidth}px` }}>
                  <SuggestionsPanel
                    candidateId={candidateId}
                    onClose={closeSuggestionsPanel}
                    onApplied={() => { loadProfile?.(); }}
                    onCountChange={setSuggestionCount}
                  />
                </div>
              )
              : activeLeftPanel === 'outreach' ? (
                <div className="bg-white flex flex-col overflow-hidden flex-shrink-0 border-r" style={{ width: `${presentationWidth}px` }}>
                  <AddToCampaignSidebar
                    candidateIds={[candidateId]}
                    pipelineId={activePipelineId || null}
                    onClose={closeOutreachPanel}
                    onEnrolled={handleEnrolled}
                  />
                </div>
              )
              : activeLeftPanel === 'messaging' ? (
                <div className="bg-white flex flex-col overflow-hidden flex-shrink-0 border-r" style={{ width: `${presentationWidth}px` }}>
                  <ConversationsTab
                    candidateId={candidateId}
                    candidateName={profile?.name || profile?.client_name || 'Candidate'}
                    candidateNumber={profile?.phone || profile?.mobile_phone || profile?.phone_number || null}
                    onClose={closeMessagingPanel}
                  />
                </div>
              )
              : renderFeedbackPanel()}
          </div>
        )}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Preview Header */}
          <div className={`flex-shrink-0 border-b px-3 h-9 flex items-center justify-between overflow-hidden ${isEditMode ? 'bg-emerald-100 border-emerald-200' : 'bg-gray-100 border-gray-200'}`}>
            <div className="flex items-center gap-2">
              {/* Creator avatar — replaces the old source badges (paper
                  airplane / building icons). Shows the profile picture
                  of whoever added or presented the candidate.
                    - source='presented' → the presenter's picture,
                      tooltip includes their firm name (cross-org "sent")
                    - source='client_created' → the CCD row's created_by,
                      tooltip is "Added by {name}"
                  Falls back to initials (first letter of name) and then
                  to a generic user glyph when nothing's available. */}
              {profile && (() => {
                const isPresented = profile.source === 'presented';
                const creatorName = isPresented
                  ? profile.presented_by_name
                  : profile.client_data?.created_by_name;
                const creatorAvatar = isPresented
                  ? profile.presented_by_avatar_url
                  : profile.client_data?.created_by_avatar_url;
                const creatorFirm = isPresented ? profile.presented_by_firm_name : null;

                // Build tooltip. Cross-org sent: "Sent by X at Y on DATE".
                // Same-org: "Added by X".
                let tooltip = 'Candidate profile';
                if (isPresented && creatorName) {
                  const dateStr = profile.presented_at
                    ? ' on ' + new Date(profile.presented_at).toLocaleDateString('en-US')
                    : '';
                  tooltip = `Sent by ${creatorName}${creatorFirm ? ` at ${creatorFirm}` : ''}${dateStr}`;
                } else if (creatorName) {
                  tooltip = `Added by ${creatorName}`;
                }

                const initial = (creatorName || '?').trim().charAt(0).toUpperCase();

                return (
                  <Tooltip text={tooltip} placement="bottom">
                    <div className="w-6 h-6 rounded-full flex-shrink-0 cursor-help overflow-hidden">
                      {creatorAvatar ? (
                        <img src={creatorAvatar} alt="" className="w-6 h-6 rounded-full object-cover" />
                      ) : creatorName ? (
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-semibold text-white ${isPresented ? 'bg-emerald-500' : 'bg-purple-500'}`}>
                          {initial}
                        </div>
                      ) : (
                        <div className="w-6 h-6 rounded-full bg-gray-300 flex items-center justify-center">
                          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                        </div>
                      )}
                    </div>
                  </Tooltip>
                );
              })()}
              {isEditMode ? (
                <div className="flex items-center gap-2"><span className="text-xs font-semibold text-emerald-700">Edit Layout</span>
                  {editModeState.hiddenWidgetsCount > 0 && <span className="px-1.5 py-0.5 bg-orange-100 text-orange-700 text-[10px] font-medium rounded-full">{editModeState.hiddenWidgetsCount} hidden</span>}
                  {editModeState.hasChanges && <span className="px-1.5 py-0.5 bg-yellow-100 text-yellow-700 text-[10px] font-medium rounded-full">Unsaved</span>}
                </div>
              ) : (
                <span className="text-xs text-gray-500 flex items-center gap-2">
                  {/* Internal reference number (per-candidate, per-firm).
                      The per-search candidate code lives in the Candidate Review
                      sidepanel since it differs per search. */}
                  {profile?.id && (
                    <PanelRefEditor
                      value={profile?.client_data?.internal_reference || ''}
                      onSave={async (newRef) => {
                        try {
                          await clientCandidateDataService.updateData(candidateId, { internal_reference: newRef });
                          setProfile(prev => prev ? { ...prev, client_data: { ...(prev.client_data || {}), internal_reference: newRef } } : prev);
                        } catch (e) { console.error('Save ref failed:', e); }
                      }}
                    />
                  )}
                </span>
              )}
            </div>
            <div className="flex items-center gap-1.5">
              {isEditMode ? (
                <>
                  <button onClick={editModeState.onReset} className="px-2 py-1 text-[11px] text-red-600 border border-red-200 rounded hover:bg-red-50">Reset</button>
                  <button onClick={editModeState.onCancel} className="px-2 py-1 text-[11px] text-gray-600 border border-gray-200 rounded hover:bg-gray-50">Cancel</button>
                  <button onClick={editModeState.onSave} disabled={editModeState.isSaving || !editModeState.hasChanges} className="px-2.5 py-1 text-[11px] bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:bg-gray-300 disabled:cursor-not-allowed font-medium">{editModeState.isSaving ? 'Saving...' : 'Save Layout'}</button>
                </>
              ) : (
                <>
                  {/* Panel-opener buttons (Suggestions, Opportunities, Outreach,
                      Messaging, Introductions, Interviews, Notes) live in the
                      mobile bottom action bar now — the panels slide up from the
                      bottom, so their triggers belong there too. Only quick
                      actions (reminder, LinkedIn, close) remain up here. */}
                  {/* Reminder dropdown — sets a follow-up reminder for
                      the currently-open candidate. Portaled to document.body
                      because the preview header uses overflow-hidden. */}
                  <div className="relative">
                    <Tooltip text="Set Reminder" placement="bottom">
                      <button
                        ref={reminderBtnRef}
                        onClick={() => {
                          setShowReminderMenu(v => {
                            const opening = !v;
                            if (opening && reminderBtnRef.current) {
                              const r = reminderBtnRef.current.getBoundingClientRect();
                              // right = distance from viewport's right edge to menu's right edge
                              setReminderMenuPos({ top: r.bottom + 4, right: window.innerWidth - r.right });
                            }
                            return opening;
                          });
                        }}
                        className={`w-6 h-6 rounded flex items-center justify-center transition-colors ${showReminderMenu ? 'text-orange-600 bg-orange-100' : 'text-gray-400 hover:text-orange-600 hover:bg-orange-50'}`}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                        </svg>
                      </button>
                    </Tooltip>
                    {showReminderMenu && createPortal(
                      <>
                        {/* Backdrop — click anywhere to close */}
                        <div
                          className="fixed inset-0 z-[9998]"
                          onClick={() => setShowReminderMenu(false)}
                        />
                        <div
                          className="fixed bg-white border rounded-lg shadow-lg w-56 z-[9999] py-1"
                          style={{ top: reminderMenuPos.top, right: reminderMenuPos.right }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="px-3 py-1.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wide border-b">Remind me in</div>
                          {[
                            { label: '2 weeks',   days: 14 },
                            { label: '1 month',   days: 30 },
                            { label: '3 months',  days: 90 },
                            { label: '6 months',  days: 180 },
                            { label: '1 year',    days: 365 },
                          ].map(opt => (
                            <button key={opt.days}
                              onClick={() => createReminder(opt.days)}
                              disabled={reminderSaving}
                              className="w-full text-left px-3 py-1.5 text-xs text-gray-700 hover:bg-orange-50 hover:text-orange-700 disabled:opacity-50"
                            >
                              {opt.label}
                            </button>
                          ))}
                          <div className="border-t px-3 py-2">
                            <label className="block text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1">Custom (days)</label>
                            <div className="flex items-center gap-1">
                              <input
                                type="number"
                                min="1"
                                placeholder="45"
                                value={reminderCustomDays}
                                onChange={(e) => setReminderCustomDays(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') { const d = parseInt(reminderCustomDays); if (d > 0) createReminder(d); } }}
                                className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-orange-500"
                              />
                              <button
                                onClick={() => { const d = parseInt(reminderCustomDays); if (d > 0) createReminder(d); }}
                                disabled={reminderSaving || !parseInt(reminderCustomDays)}
                                className="px-2 py-1 text-[10px] bg-orange-600 text-white rounded hover:bg-orange-700 disabled:opacity-50"
                              >
                                Set
                              </button>
                            </div>
                          </div>
                          {/* Optional reason — gives the recruiter context
                              when the reminder fires later. Sent as `note`
                              in the POST body; server stores nullable. */}
                          <div className="border-t px-3 py-2">
                            <label className="block text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1">Reason (optional)</label>
                            <textarea
                              rows={2}
                              placeholder="e.g. check hiring signal"
                              value={reminderNote}
                              onChange={(e) => setReminderNote(e.target.value)}
                              className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-orange-500 resize-none"
                            />
                          </div>
                          {reminderMessage && (
                            <div className="border-t px-3 py-1.5 text-[10px] text-emerald-700 bg-emerald-50">
                              {reminderMessage}
                            </div>
                          )}
                        </div>
                      </>,
                      document.body
                    )}
                  </div>
                  <Tooltip text="Call Candidate" placement="bottom">
                    <button onClick={startCall} className="w-6 h-6 rounded flex items-center justify-center text-gray-400 hover:text-emerald-700 hover:bg-emerald-50 transition-colors">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
                    </button>
                  </Tooltip>
                  {/* Sync from LinkedIn — was the LinkedIn logo, now
                      distinct circular-arrow icon so the logo can serve
                      as a link to the actual profile URL. Spins during
                      the sync request. */}
                  <Tooltip text={linkedinSyncing ? linkedinSyncStatus : "Sync from LinkedIn"} placement="bottom">
                    <button onClick={handleSyncLinkedIn} disabled={linkedinSyncing} className="w-6 h-6 rounded flex items-center justify-center text-gray-400 hover:text-[#0A66C2] hover:bg-emerald-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                      <svg className={`w-4 h-4 ${linkedinSyncing ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                    </button>
                  </Tooltip>
                  {/* LinkedIn logo — now opens the candidate's actual
                      LinkedIn URL in a new tab. Sourced defensively from
                      client_data.linkedins (edited array), then falling
                      back to the base candidate record. Disabled state
                      when no URL is on file. */}
                  {(() => {
                    const linkedinUrl = profile?.client_data?.linkedins?.[0]?.url || profile?.linkedin_url || profile?.linkedin;
                    if (linkedinUrl) {
                      return (
                        <Tooltip text="Open LinkedIn Profile" placement="bottom">
                          <a href={linkedinUrl} target="_blank" rel="noopener noreferrer" className="w-6 h-6 rounded flex items-center justify-center text-gray-400 hover:text-[#0A66C2] hover:bg-emerald-50 transition-colors">
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                          </a>
                        </Tooltip>
                      );
                    }
                    return (
                      <Tooltip text="No LinkedIn URL on file" placement="bottom">
                        <button disabled className="w-6 h-6 rounded flex items-center justify-center text-gray-300 cursor-not-allowed">
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                        </button>
                      </Tooltip>
                    );
                  })()}
                  {/* Notes gets a visible text label AND a permanent
                      background color (unlike every other button in
                      this row) — it's the primary annotation action and
                      worth surfacing explicitly. Desktop only — mobile
                      Notes lives in the bottom action bar. */}
                  {!isMobile && (
                  <button
                    onClick={openNotesPanel}
                    className={`h-6 rounded flex items-center gap-1.5 px-1.5 transition-colors ${isNotesPanelOpen ? 'text-purple-700 bg-purple-200' : 'text-purple-600 bg-purple-100 hover:bg-purple-200'}`}
                    title="Notes"
                  >
                    <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                    <span className="text-xs font-medium">Notes</span>
                  </button>
                  )}
                  {/* Close — MOBILE ONLY. On desktop it lives at the
                      top of the vertical nav column. Keeps the same
                      unsaved-changes prompt. */}
                  {isMobile && (
                    <button onClick={() => { if (isEditMode && editModeState.hasChanges) { if (window.confirm('Unsaved changes. Discard?')) { editModeState.onCancel?.(); onClose(); } } else { if (isEditMode) editModeState.onCancel?.(); onClose(); } }} className="w-6 h-6 rounded flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors" title="Close (Esc)">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
          <div className="flex-1 overflow-y-auto bg-gray-50">
            {loading ? <div className="flex items-center justify-center h-64"><div className="text-center"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-600 mx-auto"></div><p className="mt-3 text-gray-500">Loading profile...</p></div></div>
            : !profile ? <div className="flex items-center justify-center h-64"><div className="text-center text-gray-500"><p>Failed to load candidate</p><button onClick={loadProfile} className="mt-2 text-emerald-700 hover:underline">Retry</button></div></div>
            : (<>
                <EnhancedCandidateHeader profile={profile} onRefresh={handleRefresh} cardId={activeCardId} pipelineId={activePipelineId} isClientUser={isClientUser} inPreviewPanel={true} />
                <div className="px-3 py-2">
                  <ColumnBasedLayout pageType={(() => { const u = JSON.parse(localStorage.getItem('user') || '{}'); return u.user_type === 'client' ? 'client_candidate_profile' : 'candidate_profile'; })()} widgets={widgets} widgetProps={widgetProps} onEditModeChange={handleEditModeChange} containerWidth={width - 32} />
                </div>
              </>)}
          </div>
        </div>

        {/* ── Desktop: Notes Panel on the right ── */}
        {!isMobile && isNotesPanelOpen && profile && (
          <>
            <ResizeHandle onMouseDown={startNotesResize} isActive={resizingTarget === 'notes'} />
            <div className="sidebar-anim-right flex-shrink-0 border-l flex flex-col overflow-hidden" style={{ width: `${notesWidth}px` }}>
              <NotesPanel isOpen={isNotesPanelOpen} onClose={() => setIsNotesPanelOpen(false)} candidateId={candidateId} candidateName={profile.name} firmId={profile.client_data?.firm_id} currentUser={JSON.parse(localStorage.getItem('user') || '{}')} embedded={true} onNoteSaved={handleNoteSaved} />
            </div>
          </>
        )}

        {/* ── Mobile: bottom action bar — all panel-opener buttons live here,
            since the panels slide up from the bottom anyway. Horizontally
            scrollable so nothing is ever cut off. Hidden while a panel/notes
            drawer is open (the drawer has its own close). ── */}
        {isMobile && !(activeLeftPanel || isNotesPanelOpen) && (
          <div className="flex-shrink-0 border-t bg-gray-100 overflow-x-auto">
            <div className="flex items-stretch gap-1 px-2 py-1.5 min-w-max">
              {!isClientUser && (
                <button onClick={openSuggestionsPanel} className="flex flex-col items-center justify-center gap-0.5 px-3 py-1 rounded text-gray-500 hover:text-amber-600 hover:bg-amber-50 relative">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" /></svg>
                  <span className="text-[10px] font-medium whitespace-nowrap">Suggest{suggestionCount > 0 ? ` (${suggestionCount})` : ''}</span>
                </button>
              )}
              {!isClientUser && (
                <button onClick={openOpportunitiesPanel} className="flex flex-col items-center justify-center gap-0.5 px-3 py-1 rounded transition-colors text-gray-500 hover:text-teal-600 hover:bg-teal-50">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                  <span className="text-[10px] font-medium whitespace-nowrap">Opportunities</span>
                </button>
              )}
              {!isClientUser && (
                <button onClick={openOutreachPanel} className="flex flex-col items-center justify-center gap-0.5 px-3 py-1 rounded transition-colors text-gray-500 hover:text-purple-600 hover:bg-purple-50">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" /></svg>
                  <span className="text-[10px] font-medium whitespace-nowrap">Outreach</span>
                </button>
              )}
              {!isClientUser && (
                <button onClick={openMessagingPanel} className="flex flex-col items-center justify-center gap-0.5 px-3 py-1 rounded transition-colors text-gray-500 hover:text-emerald-700 hover:bg-emerald-50">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.86 9.86 0 01-4-.83l-5 1.5 1.5-4.5A7.94 7.94 0 013 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>
                  <span className="text-[10px] font-medium whitespace-nowrap">Messaging</span>
                </button>
              )}
              {!isClientUser && isPipelineContext && (
                <button onClick={openPresentationPanel} className="flex flex-col items-center justify-center gap-0.5 px-3 py-1 rounded transition-colors text-gray-500 hover:text-yellow-700 hover:bg-yellow-50">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>
                  <span className="text-[10px] font-medium whitespace-nowrap">Introductions</span>
                </button>
              )}
              {isPipelineContext && (
                <button onClick={openFeedbackPanel} className="flex flex-col items-center justify-center gap-0.5 px-3 py-1 rounded transition-colors text-gray-500 hover:text-blue-600 hover:bg-blue-50">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h9m4-4l2 2 4-4" /></svg>
                  <span className="text-[10px] font-medium whitespace-nowrap">Interviews</span>
                </button>
              )}
              <button onClick={openNotesPanel} className="flex flex-col items-center justify-center gap-0.5 px-3 py-1 rounded text-purple-600 hover:bg-purple-50">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                <span className="text-[10px] font-medium whitespace-nowrap">Notes</span>
              </button>
            </div>
          </div>
        )}

        {/* ── Mobile: bottom drawer — at most ONE of these is rendered at a time ── */}
        {isMobile && (activeLeftPanel || isNotesPanelOpen) && (
          <div className="sidebar-anim-up flex-shrink-0 border-t bg-white flex flex-col overflow-hidden" style={{ height: '65vh' }}>
            {activeLeftPanel === 'presentation' && renderPresentationPanel()}
            {activeLeftPanel === 'opportunities' && renderOpportunitiesPanel()}
            {activeLeftPanel === 'outreach' && (
              <AddToCampaignSidebar
                candidateIds={[candidateId]}
                pipelineId={activePipelineId || null}
                onClose={closeOutreachPanel}
                onEnrolled={handleEnrolled}
              />
            )}
            {activeLeftPanel === 'messaging' && (
              <ConversationsTab
                candidateId={candidateId}
                candidateName={profile?.name || profile?.client_name || 'Candidate'}
                candidateNumber={profile?.phone || profile?.mobile_phone || profile?.phone_number || null}
                onClose={closeMessagingPanel}
              />
            )}
            {activeLeftPanel === 'suggestions' && (
              <SuggestionsPanel
                candidateId={candidateId}
                onClose={closeSuggestionsPanel}
                onApplied={handleRefresh}
                onCountChange={setSuggestionCount}
              />
            )}
            {activeLeftPanel === 'feedback' && renderFeedbackPanel()}
            {!activeLeftPanel && isNotesPanelOpen && profile && (
              <NotesPanel isOpen={isNotesPanelOpen} onClose={() => setIsNotesPanelOpen(false)} candidateId={candidateId} candidateName={profile.name} firmId={profile.client_data?.firm_id} currentUser={JSON.parse(localStorage.getItem('user') || '{}')} embedded={true} onNoteSaved={handleNoteSaved} />
            )}
          </div>
        )}
      </div>

      {/* Phone Picker */}
      {showPhonePicker && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-xs">
            <div className="px-4 py-3 border-b flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-900">Select Number to Call</h3>
              <button onClick={() => setShowPhonePicker(false)} className="text-gray-400 hover:text-gray-600">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <div className="p-2 space-y-1">
              {candidatePhones.map((p, i) => (
                <button key={i} onClick={() => selectPhoneAndCall(p.value)}
                  className="w-full text-left px-3 py-2.5 rounded-lg hover:bg-emerald-50 flex items-center justify-between group">
                  <div>
                    <div className="text-sm font-mono text-gray-900">{p.value}</div>
                    <div className="text-[10px] text-gray-400">{p.type || 'Phone'}{p.is_primary ? ' · Primary' : ''}</div>
                  </div>
                  <svg className="w-4 h-4 text-emerald-500 opacity-0 group-hover:opacity-100" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
                </button>
              ))}
              <button onClick={() => { setShowPhonePicker(false); setCallPhoneNumber(''); setShowCallWidget(true); }}
                className="w-full text-left px-3 py-2 rounded-lg hover:bg-gray-50 text-xs text-gray-500">
                Dial a different number...
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Call Widget */}
      {showCallWidget && (
        <div className="fixed bottom-4 right-4 z-50">
          <CallWidget
            candidateId={candidateId}
            candidateName={profile?.name || cardData?.name || ''}
            phoneNumber={callPhoneNumber}
            onClose={() => setShowCallWidget(false)}
            onCallComplete={() => setShowCallWidget(false)}
          />
        </div>
      )}

    </>
  );
};

// PanelRefEditor — inline-editable monospace chip for the candidate's
// internal reference number, shown in the preview panel's top header bar.
// Empty state shows "+ ref"; populated state shows the value as a subtle pill.
const PanelRefEditor = ({ value, onSave }) => {
  const [isEditing, setIsEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(value || '');
  const inputRef = React.useRef(null);

  React.useEffect(() => { setDraft(value || ''); }, [value]);
  React.useEffect(() => {
    if (isEditing && inputRef.current) { inputRef.current.focus(); inputRef.current.select(); }
  }, [isEditing]);

  const commit = async () => {
    if (draft === (value || '')) { setIsEditing(false); return; }
    try { await onSave(draft); } catch (e) { console.error(e); }
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <input
        ref={inputRef}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') { setDraft(value || ''); setIsEditing(false); } }}
        placeholder="Your ID"
        className="px-1.5 py-0.5 text-[10px] font-mono bg-white border border-emerald-400 rounded focus:outline-none focus:ring-1 focus:ring-emerald-500 w-24"
      />
    );
  }

  return (
    <>
      <span className="text-[10px] uppercase tracking-wider text-gray-400 font-medium">Ref</span>
      <button
        onClick={(e) => { e.stopPropagation(); setIsEditing(true); }}
        className={`px-1.5 py-0.5 text-[10px] font-mono rounded transition-colors ${
          value
            ? 'bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100 hover:border-gray-300'
            : 'text-gray-400 italic border border-dashed border-gray-300 hover:text-gray-600 hover:border-gray-400'
        }`}
        title="Internal reference number"
      >
        {value || '+ ref'}
      </button>
    </>
  );
};

// PanelCodeEditor — inline-editable monospace chip for the per-search candidate
// code, shown in the preview panel's top header bar. Distinct from the ref number
// because the code is per-pipeline-card (different for each search the candidate
// is linked to). Empty state shows "+ code"; populated state shows the value as
// a tinted blue pill (different color from ref so the two are easy to tell apart).
const PanelCodeEditor = ({ value, pipelineName, onSave }) => {
  const [isEditing, setIsEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(value || '');
  const inputRef = React.useRef(null);

  React.useEffect(() => { setDraft(value || ''); }, [value]);
  React.useEffect(() => {
    if (isEditing && inputRef.current) { inputRef.current.focus(); inputRef.current.select(); }
  }, [isEditing]);

  const commit = async () => {
    if (draft === (value || '')) { setIsEditing(false); return; }
    try { await onSave(draft.trim().toUpperCase()); } catch (e) { console.error(e); }
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <input
        ref={inputRef}
        value={draft}
        onChange={(e) => setDraft(e.target.value.toUpperCase())}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') { setDraft(value || ''); setIsEditing(false); } }}
        placeholder="VP-01"
        className="px-1.5 py-0.5 text-[10px] font-mono bg-white border border-emerald-400 rounded focus:outline-none focus:ring-1 focus:ring-emerald-500 w-20"
      />
    );
  }

  return (
    <>
      <span className="text-[10px] uppercase tracking-wider text-gray-400 font-medium">Code</span>
      <button
        onClick={(e) => { e.stopPropagation(); setIsEditing(true); }}
        className={`px-1.5 py-0.5 text-[10px] font-mono rounded transition-colors ${
          value
            ? 'bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 hover:border-emerald-300'
            : 'text-gray-400 italic border border-dashed border-gray-300 hover:text-gray-600 hover:border-gray-400'
        }`}
        title={pipelineName ? `Code for "${pipelineName}"` : 'Search-specific candidate code'}
      >
        {value || '+ code'}
      </button>
    </>
  );
};

export default CandidatePreviewPanel;